import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { capitalize } from "../../helpers/capitalize";
import moment from "moment";
import { toast } from "react-toastify";
import Calendar from "../../components/calendar/Calendar";
import {
  getGuarantors,
  getLoanApprovals,
  deleteGuarantor,
  overrideGuarantorStatus,
  approveLoan,
  getMemberDetails,
  disburseFunds,
  declineDisburse,
  bankRecords,
  loanApprovals,
  addLoanPaymentManually,
  respondWithProof,
  uploadStatementFile,
  analyseLoanStatementByUrl,
  getLoanStatements,
} from "../../sdk/loans/loans";
import { getCustomerAccounts, uploadFile } from "../../sdk/customers/customers";
import { convertLoanDates, convertDateOfBirth } from "../../hooks/convertDate";
import GuarantorDetails from "../../components/Modals/guarantorship/guarantorDetails";
import AcceptGuarantorship from "../../components/Modals/guarantorship/acceptGuarantorship";
import RejectGuarantorship from "../../components/Modals/guarantorship/rejectGuarantorship";
import RejectSuccess from "../../components/Modals/guarantorship/rejectSuccess";
import AcceptSuccess from "../../components/Modals/guarantorship/acceptSuccess";
import RemoveGuarantor from "../../components/Modals/guarantorship/removeGuarantor";
import WarningRemoveGuarantor from "../../components/Modals/guarantorship/warningRemoveGuarantor";
import GuarantorSuccess from "../../components/Modals/guarantorship/guarantorSuccessModal";
import OverrideGuarantor from "../../components/Modals/guarantorship/overideGuarantor";
import ReasonsForOverriding from "../../components/Modals/guarantorship/reasonsForOverriding";
import OverrideWarning from "../../components/Modals/guarantorship/overrideWarning";
import OverrideSuccess from "../../components/Modals/guarantorship/overrideSuccess";
import StatusDetails from "../../components/Modals/guarantorship/statusDetails";
import DetailsPage from "../../components/Modals/approveNormalLoan/detailsPage";
import AmountReview from "../../components/Modals/approveNormalLoan/amountReview";
import LowerLoanAmount from "../../components/Modals/approveNormalLoan/lowerLoanAmount";
import ApproveLoanAmount from "../../components/Modals/approveNormalLoan/loanAmountApproval";
import ApproversDetails from "../../components/Modals/approveNormalLoan/approversDetails";
import SuccessfullyApproved from "../../components/Modals/approveNormalLoan/successfullyApproved";
import LoanChecklist from "../../components/Modals/approveNormalLoan/loanChecklist";
import FinalizeLoanApproval from "../../components/Modals/approveNormalLoan/finalizeLoanApproval";
import FinalizeDecliningLoan from "../../components/Modals/approveNormalLoan/finalizeDecliningLoan";
import LoanAddTransactions from "../../components/Modals/transactions/loanAddTransactions";
import DeclineLoan from "../../components/Modals/approveNormalLoan/declineLoan";
import DeclineSuccessful from "../../components/Modals/approveNormalLoan/declineSuccess";
import { getSingleLoanCustomer, paymentHistory } from "../../sdk/loans/loans";
import LoanProgress from "../../components/progressBar/LoanProgress";
import LoadingButtons from "../../components/Modals/edit-customer/LoadingButtons";
import MpesaConfirmModal from "../../components/Modals/disburse-loan/MpesaConfirmModal";
import ConfirmDisburseModal from "../../components/Modals/disburse-loan/ConfirmDisburseModal";
import UploadFinancialStatement from "../../components/Modals/loanAppraisial/UploadFinacialStatement";
import AnalyzingStatement from "../../components/Modals/loanAppraisial/AnalyzingStatement";
import GeneratingReports from "../../components/Modals/loanAppraisial/GeneratingReports";
import AnalysisSuccesfull from "../../components/Modals/loanAppraisial/AnalysisSuccessful";
import ConfirmCustomerDisburseModal from "../../components/Modals/disburse-loan/ConfirmCustomerDisburse";
import DisbursementRecordModal from "../../components/Modals/disburse-loan/DisbursementRecordModal";
import SuccessDisburseModal from "../../components/Modals/disburse-loan/SuccessDisburse";
import DeclineLoanModal from "../../components/Modals/disburse-loan/DeclineLoanModal";
import PrepareLoanDisbursement from "../../components/Modals/disburse-loan/PrepareLoanDisbursement";
import ChooseDisbursementMethod from "../../components/Modals/disburse-loan/ChooseDisbursementMethod";
import ConfirmLoanDeclineModal from "../../components/Modals/disburse-loan/ConfirmLoanDeclineModal";
import SuccessDeclineModal from "../../components/Modals/disburse-loan/SuccessDecline";
import DisbursingModal from "../../components/Modals/bulk-repayment/DisbursingModal";
import SuccessRecordedModal from "../../components/Modals/disburse-loan/SuccessRecorded";
import PaymentAddedSuccessfully from "../../components/Modals/transactions/paymentAddedSuccessfully";
import ViewLoanTransModal from "../../components/Modals/transactions/ViewLoanTransModal";
import ViewReceipt from "../../components/Modals/transactions/viewReceipt";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import useAudit from "../../hooks/useAudit";

const Loan = () => {
  const { loanCode, customerId, loanType } = useParams();

  const { logAudit } = useAudit();

  useEffect(() => {
    logAudit({
      category: "Loans",
      actionCode: "VIEW_LOAN_DETAILS",
      description: `Viewed loan account (Loan ID ${loanCode})`,
      page: "Loans",
      tab: "Loan Profile",
      section: "Loan Details",
      metadata: {
        loanCode,
        loanType,
      },
    });
  }, []);

  const navigate = useNavigate();
  const today = moment();
  const [fromDate, setFromDate] = useState(today.clone().startOf("month"));
  const [customerFocusedInput, setCustomerFocusedInput] = useState(null);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(null);

  const [search, setSearch] = useState("");
  const [isLoadingButtons, setIsLoadingButtons] = useState(false);
  const [isAnalyzingStatement, setIsAnalyzingStatement] = useState(false);
  const [isGeneratingReports, setIsGeneratingReports] = useState(false);

  const [guarantors, setGuarantors] = useState({});
  const [statements, setStatements] = useState([]);

  const [selectedGuarantor, setSelectedGuarantor] = useState(null);

  useEffect(() => {
    fetchGuarantors(loanCode);
    fetchLoanApprovals(loanCode);
    fetchStatements(loanCode);
  }, [loanCode]);

  const fetchGuarantors = async (loanCode) => {
    try {
      const response = await getGuarantors(loanCode);

      setGuarantors(response?.data?.guarantors);
    } catch (error) {
      console?.error(
        error?.response?.data?.message || "Error fetching guarantors",
      );
    }
  };
  const fetchStatements = async (loanCode) => {
    try {
      const response = await getLoanStatements(loanCode);

      // The API returns data as an array under response.data.data
      setStatements(response?.data || []);
    } catch (error) {
      console.error(
        error?.response?.data?.message || "Error fetching loan statements",
      );
    }
  };

  const [progress, setProgress] = useState(0);

  const fetchLoanApprovals = async (loanCode) => {
    try {
      const response = await getLoanApprovals(loanCode);
      setProgress(response?.data?.length || 0);
      setRequestApprovals(response?.data);
    } catch (error) {
      console?.error(error?.response?.message || "Error fetching data");
    }
  };

  const navigateToCustomer = (id) => {
    navigate(`/dashboard/customers/${id}`);
  };

  const navigateBack = () => {
    navigate(-1);
  };
  const gradientStyle = {
    background: "#074073",
  };

  const [activePage, setActivePage] = useState("customer");

  const containerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    paddingLeft: "10px",
  };
  const [transactions, setTransactions] = useState([]);

  const [loanCustomer, setLoanCustomer] = useState({});

  const [customerDetails, setCustomerDetails] = useState({});
  const [mpesaNumber, setMpesaNumber] = useState("");
  const [accountId, setAccountId] = useState("");

  const [approvals, setApprovals] = useState([]);
  const [requestApprovals, setRequestApprovals] = useState([]);
  const [amount, setAmount] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const fileInputFront = useRef(null);
  const [bankName, setBankName] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [amountDisbursed, setAmountDisbursed] = useState("");
  const [date, setDate] = useState("");
  const [disbursedAt, setDisbursedAt] = useState("");

  const [declineText, setDeclineText] = useState("");
  const [declineFile, setDeclineFile] = useState(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [lowerAmount, setLowerAmount] = useState(
    loanCustomer?.loan_amount || "",
  );

  useEffect(() => {
    setLowerAmount(loanCustomer?.loan_amount || "");
  }, [loanCustomer]);

  const [loanSchedule, setLoanSchedule] = useState([]);

  const [hidden, setHidden] = useState(false);
  const style = {
    display: hidden ? "none" : "block",
  };

  useEffect(() => {
    const fetchApprovals = async () => {
      try {
        if (!loanCode) return;
        const response = await loanApprovals(loanCode);

        if (Array.isArray(response?.data)) {
          setApprovals(response?.data);
        } else {
          console.warn("Unexpected response structure:", response);
        }
      } catch (error) {
        console.error("Error fetching approvals:", error);
      }
    };

    fetchApprovals();
  }, [loanCode]);

  const handlePhotoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
    }
  };

  useEffect(() => {
    setIsButtonDisabled(!declineText.trim());
  }, [declineText]);

  const handleSubmitBankRecords = async () => {
    try {
      const formData = new FormData();
      formData.append("loanCode", loanCode);
      formData.append("bankName", bankName);
      formData.append("bankAccountNumber", bankAccountNumber);
      formData.append("referenceNumber", referenceNumber);
      formData.append("amountDisbursed", amountDisbursed);
      formData.append("disbursedAt", disbursedAt);

      if (photoFile) {
        formData.append("file", photoFile);
      } else {
        console.log("No file selected!");
      }

      const result = await bankRecords(formData);

      if (result.statusCode === 200 || result.statusCode === 201) {
        toast.success("Bank records submitted successfully.");
        setIsDisbursementRecordModalOpen(false);
        setisSuccessRecordedModalOpen(true);
      } else {
        console.log("Submission failed with status:", result.status);
      }
    } catch (error) {
      console.error("Error submitting bank records:", error);
    }
  };

  // const [isHidden, setIsHidden] = useState(
  //   localStorage.getItem("loanDeclined") === "true"
  // );

  const handleSubmit = async (loanCode, description, file) => {
    if (!description) {
      toast.error("Please provide a description for declining the loan.");
      return;
    }

    setLoading(true);
    try {
      const response = await declineDisburse(loanCode, description, file);

      if (response.statusCode === 201) {
        toast.success("Loan disbursement declined successfully.");
        setIsConfirmLoanDeclineModalOpen(false);
        setisSuccessDeclineModalOpen(true);
        await fetchSingleLoanUser(customerId, loanCode, loanType);
        localStorage.setItem("loanDeclined", "true");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to decline loan disbursement.");
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = (date) => {
    const momentDate = moment(date);
    const newDate = momentDate.format("Do MMMM  YYYY");
    return newDate;
  };

  const fetchSingleLoanUser = async (customer_id, loan_code, product_id) => {
    try {
      const res = await getSingleLoanCustomer(
        customer_id,
        loan_code,
        product_id,
      );
      setLoanCustomer(res?.data);
      setMpesaNumber(res?.data?.customer?.mobileno);
      setAmount(res?.data?.loan_amount);
      setAccountId(res?.data?.account_data?.id);
      setLoanSchedule(res?.data?.loan_schedule);
    } catch (error) {
      console.error("Error fetching single loan user:", error);
    }
  };

  useEffect(() => {
    if (customerId && loanCode && loanType) {
      fetchSingleLoanUser(customerId, loanCode, loanType);
    }
  }, [customerId, loanCode, loanType]);

  const loan_data = [
    {
      Payment_Status: "PAID",
      Schedule_Date: `Loan applied on ${loanCustomer?.loan_date}`,
      Schedule_Amount: `KES ${loanCustomer?.loan_amount}`,
    },
    {
      Payment_Status: "PAID",
      Schedule_Date: `Loan approved on ${loanCustomer?.loan_approval_date}`,
      Schedule_Amount: `KES ${loanCustomer?.loan_amount}`,
    },
    {
      Payment_Status: "PAID",
      Schedule_Date: `Loan disbursed on ${loanCustomer?.loan_disbursement_date}`,
      Schedule_Amount: `KES ${loanCustomer?.loan_amount}`,
    },
  ];

  useEffect(() => {
    if (loanCustomer && loanCustomer?.loan_schedule) {
      // Constructing loan data with approval and disbursement dates
      const totalSummary = [...loan_data, ...loanCustomer?.loan_schedule];
      // Update loan schedule
      setLoanSchedule(totalSummary);
    }
  }, [loanCustomer]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const data = await getMemberDetails(customerId);
        setCustomerDetails(data);
      } catch (err) {
        console.error("Error fetching member details", err);
      }
    };

    if (customerId) {
      fetchCustomerDetails();
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomerAccounts(customerId);
  }, [customerId]);

  const [customerAccounts, setCustomerAccounts] = useState({});

  const fetchCustomerAccounts = async (customerId) => {
    const res = await getCustomerAccounts(customerId);
    setCustomerAccounts(res?.data);
  };

  const handleViewMore = (schedule) => {
    setLoanSchedule(schedule);
    setHidden(true);
  };

  const fetchData = async () => {
    try {
      const data = await paymentHistory(customerId, loanCode, loanType);
      setTransactions(data?.data?.[0]?.payment_data ?? []);
    } catch (error) {
      console?.error("Error fetching payment history:", error);
    }
  };

  useEffect(() => {
    fetchData(loanType, loanCode);
  }, [customerId, loanType, loanCode]);
  //disburse loan
  // const [isDisburseHidden, setIsDisburseHidden] = useState(
  //   localStorage.getItem("loanDisbursed") === "true"
  // );
  const disburseLoan = async (
    amount,
    loanCode,
    customerId,
    account_id,
    mpesaNumber,
  ) => {
    try {
      setIsDisbursingModalOpen(true);

      const payload = {
        amount,
        loan_code: loanCode,
        conversation_id: customerId,
        account_id: accountId,
        mobile: mpesaNumber,
      };

      const response = await disburseFunds(payload);

      if (response?.statusCode === 200) {
        setIsConfirmCustomerDisburseModalOpen(false);
        fetchSingleLoanUser(customerId, loanCode, loanType);

        setIsSuccessDisburseModalOpen(true);
        setTimeout(() => {
          setIsSuccessDisburseModalOpen(false);
        }, 1000);

        logAudit({
          category: "Loans",
          actionCode: "DISBURSE_LOAN",
          description: `Disbursed loan (Loan ID ${loanCode})`,
          page: "Loans",
          tab: "Disbursement",
          section: "Loan Details",
          metadata: {
            loanCode,
            loanType,
            amount,
          },
        });
        toast.success("Loan disbursed successfully.");
      }
    } catch (error) {
      logAudit({
        category: "Loans",
        actionCode: "DISBURSE_LOAN_FAILED",
        description: `Failed to disburse loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Disbursement",
        section: "Loan Details",
        metadata: {
          loanCode,
          loanType,
          amount,
          error: error?.message || "Loan disbursement failed.",
        },
      });

      toast?.error("Loan disbursement failed.");
    } finally {
      setTimeout(() => {
        setIsDisbursingModalOpen(false);
      }, 400);
    }
  };

  const [isGuarantorDetailsOpen, setIsGuarantorDetailsOpen] = useState(false);
  const openGuarantorDetailsOpen = () => {
    setIsGuarantorDetailsOpen(true);
  };
  const closeGuarantorDetailsOpen = () => {
    setIsGuarantorDetailsOpen(false);
  };

  const [isRemoveGuarantorOpen, setIsRemoveGuarantorOpen] = useState(false);

  const openRemoveGuarantor = () => {
    setIsRemoveGuarantorOpen(true);
  };

  const closeRemoveGuarantor = () => {
    setIsRemoveGuarantorOpen(false);
  };

  const [isWarningRemoveGuarantorOpen, setIsWarningRemoveGuarantorOpen] =
    useState(false);

  const closeWarningRemoveGuarantor = () => {
    setIsWarningRemoveGuarantorOpen(false);
  };

  const [isGuarantorSuccessOpen, setIsGuarantorSuccessOpen] = useState(false);
  const closeGuarantorSuccess = () => {
    setIsGuarantorSuccessOpen(false);
  };

  const [isOverrideGuarantorOpen, setIsOverrideGuarantorOpen] = useState(false);

  const openOverrideGuarantor = () => {
    setIsOverrideGuarantorOpen(true);
  };

  const closeOverrideGuarantor = () => {
    setIsOverrideGuarantorOpen(false);
  };

  const [isStatusDetailsOpen, setIsStatusDetailsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  const openStatusDetails = () => {
    setIsStatusDetailsOpen(true);
  };

  const closeStatusDetails = () => {
    setIsStatusDetailsOpen(false);
  };

  const [isReasonsForOverridingOpen, setIsReasonsForOverridingOpen] =
    useState(false);
  const [note, setNote] = useState("");
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");

  const openReasonsForOverriding = () => {
    setIsReasonsForOverridingOpen(true);
  };

  const closeReasonsForOverriding = () => {
    setIsReasonsForOverridingOpen(false);
  };

  const [isOverrideWarningOpen, setIsOverrideWarningOpen] = useState(false);

  const openOverrideWarning = () => {
    setIsOverrideWarningOpen(true);
  };

  const closeOverrideWarning = () => {
    setIsOverrideWarningOpen(false);
  };

  const [isOverrideSuccessOpen, setIsOverrideSuccessOpen] = useState(false);

  const closeOverrideSuccess = () => {
    setIsOverrideSuccessOpen(false);
  };

  const handleDeleteGuarantor = async () => {
    setIsWarningRemoveGuarantorOpen(false);
    setIsLoadingButtons(true);

    try {
      await deleteGuarantor({ guarantorRequestId: selectedGuarantor?.id });

      await fetchGuarantors(loanCode);

      setIsLoadingButtons(false);
      setIsGuarantorSuccessOpen(true);
      setIsGuarantorDetailsOpen(false);

      setTimeout(() => {
        setIsGuarantorSuccessOpen(false);
      }, 1000);
    } catch (error) {
      setIsLoadingButtons(false);
      toast?.error(error?.message || "Error deleting guarantor", error);
    }
  };

  const [identityVerified, setIdentityVerified] = useState("Yes");
  const [kycValid, setKycValid] = useState("Yes");
  const [guarantorVerified, setGuarantorVerified] = useState("Yes");
  const [savingsRequirementMet, setSavingsRequirementMet] = useState("No");
  const [noOutstandingDefaults, setNoOutstandingDefaults] = useState("Yes");
  const [membershipTenureMet, setMembershipTenureMet] = useState("Yes");
  const [documentsReviewed, setDocumentsReviewed] = useState("Yes");
  const [frontFile, setFrontFile] = useState(null);

  const handleOverrideGuarantorStatus = async (status, responseReason) => {
    setIsOverrideWarningOpen(false);

    setIsLoadingButtons(true);

    try {
      await overrideGuarantorStatus({
        guarantorRequestId: selectedGuarantor?.id,
        status: selectedOption,
        responseReason: note,
      });

      await fetchGuarantors(loanCode);

      setIsLoadingButtons(false);
      setIsOverrideSuccessOpen(true);
      setIsGuarantorDetailsOpen(false);

      setTimeout(() => {
        setIsOverrideSuccessOpen(false);
      }, 1000);
    } catch (error) {
      setIsLoadingButtons(false);
      toast.error(error?.message || "Error updating guarantor status", error);
    }
  };

  const [isDetailsPageOpen, setIsDetailsPageOpen] = useState(false);

  const openDetailsPageOpen = () => {
    setIsDetailsPageOpen(true);
  };

  const closeDetailsPageOpen = () => {
    setIsDetailsPageOpen(false);
  };
  //member details/loan details
  const [
    isPrepareLoanDisbursementModalOpen,
    setIsPrepareLoanDisbursementModalOpen,
  ] = useState(false);

  const openPrepareLoanDisbursementModalOpen = () => {
    setIsPrepareLoanDisbursementModalOpen(true);
  };
  const closePrepareLoanDisbursementModal = () => {
    setIsPrepareLoanDisbursementModalOpen(false);
  };
  const declineDisbursment = () => {
    setIsPrepareLoanDisbursementModalOpen(false);
    setIsDeclineLoanModalOpen(true);
  };
  //choose disburse method
  const [isChooseDisbursementMethodOpen, setIsChooseDisbursementMethodOpen] =
    useState(false);
  const openChooseDisbursementMethodOpen = () => {
    setIsChooseDisbursementMethodOpen(true);
  };
  const closeChooseDisbursementMethod = () => {
    setIsChooseDisbursementMethodOpen(false);
  };
  const handleDisbursementSelection = (option) => {
    if (option === "M-PESA") {
      setIsMpesaConfirmModalOpen(true);
    } else if (option === "BANK") {
      setIsDisbursementRecordModalOpen(true);
    }
    setIsChooseDisbursementMethodOpen(false);
  };
  const handleChooseBack = () => {
    setIsChooseDisbursementMethodOpen(false);
    setIsPrepareLoanDisbursementModalOpen(true);
  };

  //mpesa modal
  const [isMpesaConfirmModalOpen, setIsMpesaConfirmModalOpen] = useState(false);

  const closeMpesaConfirmModal = () => {
    setIsMpesaConfirmModalOpen(false);
  };
  //mpesa to member/loan details
  const handleConfirmMpesaBack = () => {
    setIsMpesaConfirmModalOpen(false);
    setIsChooseDisbursementMethodOpen(true);
  };

  // open confirm with warning modal
  const handleDisburse = async () => {
    setIsMpesaConfirmModalOpen(false);

    setIsConfirmDisburseModalOpen(true);
  };

  //confirm with warning
  const [isConfirmDisburseModalOpen, setIsConfirmDisburseModalOpen] =
    useState(false);
  const closeConfirmDisburseModal = () => {
    setIsConfirmDisburseModalOpen(false);
  };

  //confirm with warning message to confirm customer
  const handleConfirmDisburse = () => {
    setIsConfirmDisburseModalOpen(false);
    setIsConfirmCustomerDisburseModalOpen(true);
  };
  //cancel confirm with warning message to mpesa confirm
  const handleConfirmDisburseCancel = () => {
    setIsConfirmDisburseModalOpen(false);
    setIsMpesaConfirmModalOpen(true);
  };
  //customer confirm modal
  const [
    isConfirmCustomerDisburseModalOpen,
    setIsConfirmCustomerDisburseModalOpen,
  ] = useState(false);
  //close confirm customer
  const closeConfirmCustomerDisburseModal = () => {
    setIsConfirmCustomerDisburseModalOpen(false);
  };
  //disbursment record modal
  const [isDisbursementRecordModalOpen, setIsDisbursementRecordModalOpen] =
    useState(false);

  //close disbursement record modal
  const closeDisbursementRecordModal = () => {
    setIsDisbursementRecordModalOpen(false);
    setIsChooseDisbursementMethodOpen(true);
  };
  //from disbursment record to success modal
  const handleDisbursementRecord = () => {
    setIsDisbursementRecordModalOpen(false);
    setIsSuccessDisburseModalOpen(true);
  };
  //success modal
  const [isSuccessDisburseModalOpen, setIsSuccessDisburseModalOpen] =
    useState(false);
  const closeSuccessDisburseModal = () => {
    setIsSuccessDisburseModalOpen(false);
  };
  //decline loan modal
  const [isDeclineLoanModalOpen, setIsDeclineLoanModalOpen] = useState(false);
  //declining disburse
  const finishDeclineDisburse = () => {
    setIsDeclineLoanModalOpen(false);
    setIsConfirmLoanDeclineModalOpen(true);
  };

  //closing deline modal
  const closeDeclineLoanModal = () => {
    setIsDeclineLoanModalOpen(false);
  };

  const [isConfirmLoanDeclineModalOpen, setIsConfirmLoanDeclineModalOpen] =
    useState(false);
  //closing confirm loan decline modal
  const closeConfirmLoanDeclineModal = () => {
    setIsConfirmLoanDeclineModalOpen(false);
  };
  //successfully declined
  const [isSuccessDeclineModalOpen, setisSuccessDeclineModalOpen] =
    useState(false);
  //closing successfully decline
  const closeSuccessDeclineModal = () => {
    setisSuccessDeclineModalOpen(false);
  };
  //disbursing
  const [isDisbursingModalOpen, setIsDisbursingModalOpen] = useState(false);

  //successfully recorded
  const [isSuccessRecordedModalOpen, setisSuccessRecordedModalOpen] =
    useState(false);
  //closing successfully recorded
  const closeSuccessRecordedModal = () => {
    setisSuccessRecordedModalOpen(false);
  };

  const [isAmountReviewOpen, setIsAmountReviewOpen] = useState(false);

  const openAmountReviewOpen = () => {
    setIsAmountReviewOpen(true);
  };

  const closeAmountReviewOpen = () => {
    setIsAmountReviewOpen(false);
  };

  const [isLowerLoanAmountOpen, setIsLowerLoanAmountOpen] = useState(false);

  const openLowerLoanAmount = () => {
    setIsLowerLoanAmountOpen(true);
  };

  const closeLowerLoanAmount = () => {
    setIsLowerLoanAmountOpen(false);
  };
  const handleCancelButtonClick = () => {
    setLowerAmount(loanCustomer?.loan_amount || "");
    closeLowerLoanAmount();
  };

  const [isApproveLoanAmountOpen, setIsApproveLoanAmountOpen] = useState(false);
  const openApproveLoanAmount = () => {
    setIsApproveLoanAmountOpen(true);
  };
  const closeApproveLoanAmount = () => {
    setIsApproveLoanAmountOpen(false);
  };

  const [isApproversDetailsOpen, setIsApproversDetailsOpen] = useState(false);
  const openApproversDetailsOpen = () => {
    setIsApproversDetailsOpen(true);
  };

  const closeApproversDetailsOpen = () => {
    setIsApproversDetailsOpen(false);
  };

  const [isLoanChecklistOpen, setIsLoanChecklistOpen] = useState(false);
  const openLoanChecklistOpen = () => {
    setIsLoanChecklistOpen(true);
  };
  const closeLoanChecklistOpen = () => {
    setIsLoanChecklistOpen(false);
  };

  const [isFinalizeLoanApprovalOpen, setIsFinalizeLoanApprovalOpen] =
    useState(false);

  const openFinalizeLoanApprovalOpen = () => {
    setIsFinalizeLoanApprovalOpen(true);
  };
  const closeFinalizeLoanApprovalOpen = () => {
    setIsFinalizeLoanApprovalOpen(false);
  };

  const [isFinalizeDecliningLoanOpen, setIsFinalizeDecliningLoanOpen] =
    useState(false);

  const openFinalizeDecliningLoanOpen = () => {
    setIsFinalizeDecliningLoanOpen(true);
  };

  const closeFinalizeDecliningLoanOpen = () => {
    setIsFinalizeDecliningLoanOpen(false);
  };

  const [isDeclineLoanOpen, setIsDeclineLoanOpen] = useState(false);
  const openDeclineLoan = () => {
    setIsDeclineLoanOpen(true);
  };
  const closeDeclineLoan = () => {
    setIsDeclineLoanOpen(false);
  };

  const [isDeclineSuccessfulOpen, setIsDeclineSuccessfulOpen] = useState(false);
  const openDeclineSuccessful = () => {
    setIsDeclineSuccessfulOpen(true);
  };
  const closeDeclineSuccessful = () => {
    setIsDeclineSuccessfulOpen(false);
  };

  const [isSuccessfullyApprovedOpen, setIsSuccessfullyApprovedOpen] =
    useState(false);

  const closeSuccessfullyApproved = () => {
    setIsSuccessfullyApprovedOpen(false);
  };

  const handleApproveLoan = async () => {
    setIsApproveLoanAmountOpen(false);
    try {
      await approveLoan({
        adminId: localStorage?.getItem("userID"),
        loancode: loanCode,
        checklist: {
          borrowerVerified: identityVerified === "Yes" ? true : false,
          documentsValid: kycValid === "Yes" ? true : false,
          guarantorsVerified: guarantorVerified === "Yes" ? true : false,
          membershipValid: membershipTenureMet === "Yes" ? true : false,
          noOutstandingDebts: noOutstandingDefaults === "Yes" ? true : false,
          memberMinimumSavings: savingsRequirementMet === "Yes" ? true : false,
          documentsCompliant: documentsReviewed === "Yes" ? true : false,
        },
        additionalDocuments: frontFile?.name,
        description: text,
        status: "approved",
        adjustedLoanAmount: Number(lowerAmount),
      });

      setIsSuccessfullyApprovedOpen(true);
      fetchLoanApprovals(loanCode);
      fetchSingleLoanUser(customerId, loanCode, loanType);
      setTimeout(() => {
        setIsSuccessfullyApprovedOpen(false);
      }, 1000);

      toast?.success("Loan approved successfully!");
      logAudit({
        category: "Loans",
        actionCode: "APPROVE_LOAN",
        description: `Approved loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Approval",
        section: "Loan Details",
        metadata: {
          loanCode,
          loanType,
        },
      });
    } catch (error) {
      logAudit({
        category: "Loans",
        actionCode: "APPROVE_LOAN_FAILED",
        description: `Failed to approve loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Approval",
        section: "Loan Details",
        metadata: {
          loanCode,
          loanType,
          error: error?.message || "Unknown error",
          adminId: localStorage?.getItem("userID"),
        },
      });

      toast?.error(error?.message || "Error approving loan:", error);
    }
  };

  const handleDeclineLoan = async () => {
    setIsDeclineLoanOpen(false);
    try {
      await approveLoan({
        adminId: localStorage?.getItem("userID"),
        loancode: loanCode,
        checklist: {
          borrowerVerified: true,
          documentsValid: true,
          guarantorsVerified: true,
          membershipValid: false,
          noOutstandingDebts: true,
          memberMinimumSavings: true,
          documentsCompliant: true,
        },
        additionalDocuments: frontFile?.name,
        description: reason,
        status: "declined",
        adjustedLoanAmount: Number(lowerAmount),
      });

      setIsDeclineSuccessfulOpen(true);
      fetchLoanApprovals(loanCode);
      fetchSingleLoanUser(customerId, loanCode, loanType);
      setTimeout(() => {
        setIsDeclineSuccessfulOpen(false);
      }, 1000);

      toast?.success("Loan declined successfully!");
      logAudit({
        category: "Loans",
        actionCode: "REJECT_LOAN",
        description: `Rejected loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Approval",
        section: "Loan Details",
        metadata: {
          loanCode,
          loanType,
          rejectionReason: reason,
        },
      });
    } catch (error) {
      logAudit({
        category: "Loans",
        actionCode: "REJECT_LOAN_FAILED",
        description: `Failed to reject loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Approval",
        section: "Loan Details",
        metadata: {
          loanCode,
          loanType,
          rejectionReason: reason,
          route: `/dashboard/loans/${loanCode}`,
          error: error?.message || "Error declining loan",
        },
      });

      toast?.error(error?.message || "Error declining loan:", error);
    }
  };

  const getPercentage = () => {
    return progress === 0
      ? "0%"
      : progress === 1
        ? "33%"
        : progress === 2
          ? "66%"
          : "100%";
  };

  const getProgressLabel = () => {
    return `(${progress}/3 LCMs)`;
  };

  const userID = localStorage?.getItem("userID");

  const hasApproved = requestApprovals?.some(
    (requestApprovals) => requestApprovals?.admin?.id === userID,
  );

  const [loanTransMode, setLoanTransMode] = useState("MPESA");
  const [loanAmount, setLoanAmount] = useState("");
  const [loanDate, setLoanDate] = useState(null);
  const [loanAccNumber, setLoanAccNumber] = useState("");
  const [loanRefNumber, setLoanRefNumber] = useState("");
  const [loanDocFile, setLoanDocFile] = useState(null);

  const [loansChecked, setLoansChecked] = useState(false);

  const [isLoanAddTransactionsOpen, setIsLoanAddTransactionsOpen] =
    useState(false);

  const openLoanAddTransactionsOpen = () => {
    setIsLoanAddTransactionsOpen(true);
  };

  const closeLoanAddTransactionsOpen = () => {
    setIsLoanAddTransactionsOpen(false);
  };

  const [isPaymentAddedSuccessfullyOpen, setIsPaymentAddedSuccessfullyOpen] =
    useState(false);

  const closePaymentAddedSuccessfully = () => {
    setIsPaymentAddedSuccessfullyOpen(false);
  };

  const handleManualLoanPayment = async () => {
    setIsLoanAddTransactionsOpen(false);
    setIsLoadingButtons(true);
    try {
      let documentUrl = null;
      const sanitizeFileName = (file) => {
        const sanitizedName = file?.name?.replace(/\s+/g, "-"); // replace spaces with -
        return new File([file], sanitizedName, { type: file?.type });
      };

      // Upload the document file first (if present)
      if (loanDocFile) {
        try {
          const sanitizedFile = sanitizeFileName(loanDocFile);
          documentUrl = await uploadFile(sanitizedFile);
        } catch (uploadError) {
          setIsLoadingButtons(false);
          toast?.error(
            uploadError?.message || "Error uploading document",
            uploadError,
          );
          return;
        }
      }

      await addLoanPaymentManually({
        deposit_method: loanTransMode,
        type: "credit",
        amount: loanAmount,
        customer_id: customerId,
        account_id: accountId,
        currency_id: "KES",
        ref_number: loanRefNumber,
        withdrawal_method: "none",
        phone_number: loanAccNumber,
        document_url: documentUrl,
        transaction_date: loanDate,
      });

      setIsLoadingButtons(false);
      fetchData(loanType, loanCode);
      setIsPaymentAddedSuccessfullyOpen(true);
      fetchSingleLoanUser(customerId, loanCode, loanType);

      // setLoanTransMode("");
      setLoanAmount("");
      setLoanDate("");
      setLoanAccNumber("");
      setLoanRefNumber("");
      setLoanDocFile(null);
      setLoansChecked(false);

      setTimeout(() => {
        setIsPaymentAddedSuccessfullyOpen(false);
      }, 1000);
      logAudit({
        category: "Loans",
        actionCode: "CREATE_MANUAL_TRANSACTION",
        description: `Created manual transaction on loan account (${loanCode})`,
        page: "Loans",
        tab: "Manual Transaction",
        section: "Transactions",
        metadata: {
          loanCode,
          amount,
        },
      });
    } catch (error) {
      logAudit({
        category: "Loans",
        actionCode: "CREATE_MANUAL_TRANSACTION_FAILED",
        description: `Failed to create manual transaction on loan account (${loanCode})`,
        page: "Loans",
        tab: "Manual Transaction",
        section: "Transactions",
        metadata: {
          loanCode,
          amount: loanAmount,
          customerId,
          accountId,
          error: error?.message || "Error adding manual transaction",
        },
      });

      setIsLoadingButtons(false);
      toast?.error(error?.message || "Error adding manual transaction", error);
    }
  };

  const [singleTransaction, setSingleTransaction] = useState({});
  const [openViewLoanTransModal, setOpenViewLoanTransModal] = useState(false);
  const openTransactionDetails = () => {
    setOpenViewLoanTransModal(true);
  };

  const closeTransactionDetails = () => {
    setOpenViewLoanTransModal(false);
  };
  const handleSingleTransaction = (transaction) => {
    setSingleTransaction(transaction);
    openTransactionDetails();
  };

  const [viewReceiptOpen, setViewReceiptOpen] = useState(false);

  const openViewReceipt = () => {
    setViewReceiptOpen(true);
    closeTransactionDetails();
  };

  const closeViewReceipt = () => {
    setViewReceiptOpen(false);
  };

  const [isAcceptGuarantorship, setIsAcceptGuarantorship] = useState(false);
  const openAcceptGuarantorship = () => {
    setIsAcceptGuarantorship(true);
  };
  const closeAcceptGuarantorship = () => {
    setIsAcceptGuarantorship(false);
  };

  const [isRejectGuarantorshipOpen, setIsRejectGuarantorshipOpen] =
    useState(false);

  const openRejectGuarantorship = () => {
    setIsRejectGuarantorshipOpen(true);
  };

  const closeRejectGuarantorship = () => {
    setIsRejectGuarantorshipOpen(false);
  };

  const [isRejectSuccessOpen, setIsRejectSuccessOpen] = useState(false);

  const closeRejectSuccess = () => {
    setIsRejectSuccessOpen(false);
  };

  const handleSubmitGuarantorResponse = async () => {
    setIsLoadingButtons(true);

    try {
      const response = await respondWithProof({
        guarantorId: selectedGuarantor?.customer_id,
        requestId: selectedGuarantor?.id,
        status: "rejected",
        amountGuaranteed: "",
        responseReason: "Rejected by guarantor",
        proofFile: null,
      });

      if (response?.statusCode === 201) {
        await fetchGuarantors(loanCode);
        setIsLoadingButtons(false);
        closeRejectGuarantorship();
        setIsRejectSuccessOpen(true);

        setTimeout(() => {
          setIsRejectSuccessOpen(false);
        }, 2500);
      }
    } catch (error) {
      setIsLoadingButtons(false);
      toast.error(error?.message || "Failed to submit guarantor response.");
    }
  };
  const [guaranteeAmount, setGuaranteeAmount] = useState("");
  const [uploadeDocfile, setUploadeDocfile] = useState(null);
  const [isAcceptSuccessOpen, setIsAcceptSuccessOpen] = useState(false);
  const closeAcceptSuccess = () => {
    setIsAcceptSuccessOpen(false);
  };

  const handleSubmitGuarantorResponseAccepted = async () => {
    setIsLoadingButtons(true);

    try {
      const response = await respondWithProof({
        guarantorId: selectedGuarantor?.customer_id,
        requestId: selectedGuarantor?.id,
        status: "accepted",
        amountGuaranteed: guaranteeAmount || "",
        responseReason: "Accepted by guarantor",
        proofFile: uploadeDocfile,
      });

      if (response?.statusCode === 201) {
        await fetchGuarantors(loanCode);
        setIsLoadingButtons(false);

        closeAcceptGuarantorship();
        setIsAcceptSuccessOpen(true);
        setGuaranteeAmount("");
        setUploadeDocfile(null);

        setTimeout(() => {
          setIsAcceptSuccessOpen(false);
        }, 2500);
      }
    } catch (error) {
      setIsLoadingButtons(false);
      toast.error(error?.message || "Failed to submit guarantor response.");
    }
  };

  const hiddenStatuses = [
    "pending",
    "pending approval",
    "approved",
    "npl",
    "cleared",
    "rejected",
    "paid-off",
  ];

  const loanStatus = loanCustomer?.loan_status?.toLowerCase();
  const [isAnalysisSuccesfullOpen, setIsAnalysisSuccesfullOpen] =
    useState(false);
  const closeAnalysisSuccesfull = () => {
    setIsAnalysisSuccesfullOpen(false);
  };

  const [statementType, setStatementType] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [statementPassword, setStatementPassword] = useState("");
  const [isUploadFinancialStatementOpen, setIsUploadFinancialStatementOpen] =
    useState(false);

  const openUploadFinancialStatementOpen = () => {
    setIsUploadFinancialStatementOpen(true);
  };
  const closeUploadFinancialStatementOpen = () => {
    setIsUploadFinancialStatementOpen(false);
  };
  const [selectedStatement, setSelectedStatement] = useState({
    statementId: null,
    customerId: null,
    url: null,
    statementType: null,
    statementCode: null,
  });
  const [statementData, setStatementData] = useState(null);
  const resolvedStatementType =
    selectedStatement?.statementType || statementType;
  const navigateToLoanApprasialReport = () => {
    closeAnalysisSuccesfull(false);

    navigate(`/dashboard/loans/${loanCode}/${resolvedStatementType}`, {
      state: { statementData },
      replace: true,
    });
  };

  const handleStatementClick = async (statement) => {
    // 1️⃣ store values
    const payload = {
      statementId: statement?.id,
      customerId: statement?.customerId,
      url: statement?.url,
      statementType: statement?.type,
      statementCode: statement?.statementCode,
    };

    setSelectedStatement(payload);

    // 2️⃣ call analyse with those values
    await handleAnalyseStatementByUrl(payload);
  };

  const handleAnalyseStatementByUrl = async ({
    statementId,
    customerId,
    url,
    statementType,
    statementCode,
  }) => {
    closeUploadFinancialStatementOpen();
    setIsGeneratingReports(true);

    try {
      const response = await uploadStatementFile({
        file_name: "statement",
        file_path: url, // ✅ from clicked row
        statement_bank_code: statementCode,
        statement_code: statementCode,
        statement_type: statementType,
        mobile_no: loanCustomer?.customer?.mobileno?.replace(/^\+/, ""),
        customer_id: customerId, // ✅ from clicked row
        statement_id: statementId, // ✅ from clicked row
        id_no: loanCustomer?.customer?.identification,
      });

      if (response?.data) {
        setStatementData(response.data);

        sessionStorage.setItem("statementData", JSON.stringify(response.data));

        setUploadedFile(null);
        setIsAnalysisSuccesfullOpen(true);
      } else {
        throw new Error("Statement analysis failed");
      }
    } catch (error) {
      toast.error(error?.message || "Error analyzing statement");
    } finally {
      setIsGeneratingReports(false);
    }
  };

  const handleUploadStatement = async () => {
    closeUploadFinancialStatementOpen();
    setIsAnalyzingStatement(true);
    try {
      let documentUrl = null;
      const sanitizeFileName = (file) => {
        const sanitizedName = file?.name?.replace(/\s+/g, "-"); // replace spaces with -
        return new File([file], sanitizedName, { type: file?.type });
      };

      // Upload the document file first (if present)
      if (uploadedFile) {
        try {
          const sanitizedFile = sanitizeFileName(uploadedFile);
          documentUrl = await uploadFile(sanitizedFile);
        } catch (uploadError) {
          setIsAnalyzingStatement(false);
          toast?.error(
            uploadError?.message || "Error uploading document",
            uploadError,
          );
          return;
        }
      }

      // Upload statement and store response in state
      const response = await analyseLoanStatementByUrl({
        file_name: "statement",
        file_path: documentUrl,
        statement_bank_code: statementPassword,
        statement_code: statementPassword,
        statement_type: statementType,
        mobile_no: loanCustomer?.customer?.mobileno?.replace(/^\+/, ""),
        customer_id: customerId,
        id_no: loanCustomer?.customer?.identification,
      });

      // ✅ Successful analysis
      if (response?.data) {
        setStatementData(response?.data);

        // persist full data for refresh safety
        sessionStorage?.setItem(
          "statementData",
          JSON?.stringify(response?.data),
        );
        setUploadedFile(null);

        // Open success modal
        setIsAnalysisSuccesfullOpen(true);
      } else {
        throw new Error("Statement analysis failed");
      }
    } catch (error) {
      setIsLoadingButtons(false);
      toast.error(error?.message || "Error in the analyzing document:", error);
    } finally {
      setIsAnalyzingStatement(false);
    }
  };

  const handleTabChange = (tab) => {
    setActivePage(tab);
    auditLoanTabView(tab);
  };

  useEffect(() => {
    if (activePage) {
      auditLoanTabView(activePage);
    }
  }, [activePage]);

  const auditLoanTabView = (tab) => {
    const auditMap = {
      customer: {
        actionCode: "VIEW_LOAN_MEMBER_DETAILS",
        description: `Viewed member details for loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Member Information",
        section: "Member Details",
        metadata: {
          loanCode,
          memberId: customerId,
        },
      },
      loanAppraisal: {
        actionCode: "VIEW_LOAN_APPRAISAL",
        description: `Viewed loan appraisal (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Loan Appraisal",
        section: "Loan Appraisal",
        metadata: {
          loanCode,
          loanType,
        },
      },

      loanBreakdown: {
        actionCode: "VIEW_LOAN_BREAKDOWN",
        description: `Viewed loan breakdown (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Loan Breakdown",
        section: "Loan Breakdown",
        metadata: {
          loanCode,
          loanStatus,
        },
      },

      guarantorship: {
        actionCode: "VIEW_LOAN_GUARANTORSHIP",
        description: `Viewed guarantorship details for loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Guarantor Information",
        section: "Guarantorship",
        metadata: {
          loanCode,
          loanType,
        },
      },
      audit: {
        actionCode: "VIEW_LOAN_AUDIT_TRAIL",
        description: `Viewed loan audit trail (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Audit Trail",
        section: "Loan Audit",
        metadata: {
          loanCode,
        },
      },
      history: {
        actionCode: "VIEW_LOAN_PAYMENT_HISTORY",
        description: `Viewed payment history for loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Repayments",
        section: "Payment History",
        metadata: {
          loanCode,
        },
      },
      actions: {
        actionCode: "VIEW_LOAN_ACTIONS",
        description: `Viewed loan actions for loan (Loan ID ${loanCode})`,
        page: "Loans",
        tab: "Loan Actions",
        section: "Actions",
        metadata: {
          loanCode,
          loanType,
        },
      },
    };

    const audit = auditMap[tab];

    if (!audit) return;

    logAudit({
      category: "Loans",
      ...audit,
    });
  };

  return (
    <div>
      <div
        className="h-[351px] w-full px-[40px] py-[15px]"
        style={gradientStyle}
      >
        <div className="flex flex-col justify-between h-full">
          <div className="relative w-full">
            <div
              onClick={navigateBack}
              className="absolute cursor-pointer rounded-[8px] text-[#074073] text-[12px] bg-[#037BD1] h-[26px] w-[70px] flex items-center justify-center gap-[5px]"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.9375 8.4375H15.1875C15.3367 8.4375 15.4798 8.49676 15.5852 8.60225C15.6907 8.70774 15.75 8.85082 15.75 9C15.75 9.14918 15.6907 9.29226 15.5852 9.39775C15.4798 9.50324 15.3367 9.5625 15.1875 9.5625H3.9375C3.78832 9.5625 3.64524 9.50324 3.53975 9.39775C3.43426 9.29226 3.375 9.14918 3.375 9C3.375 8.85082 3.43426 8.70774 3.53975 8.60225C3.64524 8.49676 3.78832 8.4375 3.9375 8.4375Z"
                  fill="#074073"
                />
                <path
                  d="M4.1704 9.00022L8.83578 13.6645C8.9414 13.7701 9.00074 13.9133 9.00074 14.0627C9.00074 14.2121 8.9414 14.3553 8.83578 14.461C8.73016 14.5666 8.5869 14.6259 8.43753 14.6259C8.28816 14.6259 8.1449 14.5666 8.03928 14.461L2.97678 9.39847C2.9244 9.34622 2.88283 9.28415 2.85448 9.21581C2.82612 9.14747 2.81152 9.07421 2.81152 9.00022C2.81152 8.92623 2.82612 8.85297 2.85448 8.78463C2.88283 8.7163 2.9244 8.65422 2.97678 8.60197L8.03928 3.53947C8.1449 3.43385 8.28816 3.37451 8.43753 3.37451C8.5869 3.37451 8.73016 3.43385 8.83578 3.53947C8.9414 3.64509 9.00074 3.78835 9.00074 3.93772C9.00074 4.08709 8.9414 4.23035 8.83578 4.33597L4.1704 9.00022Z"
                  fill="#074073"
                />
              </svg>

              <p>Back</p>
            </div>
            <div className="bg-[#F0FFFE] h-[44px] absolute right-[0px] rounded-[30px] px-5 flex items-center justify-center gap-[10px]">
              {loanCustomer?.loan_status === "Active" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <g clip-path="url(#clip0_8496_1672)">
                    <path
                      d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
                      fill="#0C722F"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_8496_1672">
                      <rect width="12" height="12" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              )}
              {loanCustomer?.loan_status === "Overdue" && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="6" cy="6" r="6" fill="#FF9F1C" />

                  <path
                    d="M6 3.2V6"
                    stroke="white"
                    strokeWidth="1"
                    strokeLinecap="round"
                  />

                  <path
                    d="M6 6L8.2 7.4"
                    stroke="white"
                    strokeWidth="1"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {loanCustomer?.loan_status === "Paid-off" && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_3697_3295)">
                    <path
                      d="M6 0C6.55078 0 7.08203 0.0703125 7.59375 0.210938C8.10547 0.351562 8.58203 0.554688 9.02344 0.820312C9.46484 1.08594 9.86914 1.39844 10.2363 1.75781C10.6035 2.11719 10.918 2.52148 11.1797 2.9707C11.4414 3.41992 11.6426 3.89844 11.7832 4.40625C11.9238 4.91406 11.9961 5.44531 12 6C12 6.55078 11.9297 7.08203 11.7891 7.59375C11.6484 8.10547 11.4453 8.58203 11.1797 9.02344C10.9141 9.46484 10.6016 9.86914 10.2422 10.2363C9.88281 10.6035 9.47852 10.918 9.0293 11.1797C8.58008 11.4414 8.10156 11.6426 7.59375 11.7832C7.08594 11.9238 6.55469 11.9961 6 12C5.44922 12 4.91797 11.9297 4.40625 11.7891C3.89453 11.6484 3.41797 11.4453 2.97656 11.1797C2.53516 10.9141 2.13086 10.6016 1.76367 10.2422C1.39648 9.88281 1.08203 9.47852 0.820312 9.0293C0.558594 8.58008 0.357422 8.10156 0.216797 7.59375C0.0761719 7.08594 0.00390625 6.55469 0 6C0 5.44922 0.0703125 4.91797 0.210938 4.40625C0.351562 3.89453 0.554688 3.41797 0.820312 2.97656C1.08594 2.53516 1.39844 2.13086 1.75781 1.76367C2.11719 1.39648 2.52148 1.08203 2.9707 0.820312C3.41992 0.558594 3.89844 0.357422 4.40625 0.216797C4.91406 0.0761719 5.44531 0.00390625 6 0ZM9.5332 4.01367L8.73633 3.2168L4.875 7.07812L3.26367 5.4668L2.4668 6.26367L4.875 8.67188L9.5332 4.01367Z"
                      fill="#0C722F"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3697_3295">
                      <rect width="12" height="12" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              )}
              {loanCustomer?.loan_status === "Rejected" && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_3697_3201"
                    maskType="luminance"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="14"
                    height="14"
                  >
                    <path
                      d="M0 0.32002C0 0.154334 0.134315 0.0200195 0.3 0.0200195H13.7C13.8657 0.0200195 14 0.154334 14 0.32002V13.6801C14 13.8458 13.8657 13.9801 13.7 13.9801H0.3C0.134315 13.9801 0 13.8458 0 13.6801V0.32002Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_3697_3201)">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M14 0.32002C14 0.154334 13.8657 0.0200195 13.7 0.0200195H0.3C0.134315 0.0200195 0 0.154334 0 0.32002V13.6801C0 13.8458 0.134315 13.9801 0.3 13.9801H13.7C13.8657 13.9801 14 13.8458 14 13.6801V0.32002ZM10.3647 3.88232C10.4159 3.82394 10.4549 3.75594 10.4793 3.68232C10.5038 3.6087 10.5133 3.53094 10.5072 3.45362C10.501 3.3763 10.4795 3.30097 10.4438 3.23208C10.408 3.16319 10.3588 3.10212 10.2991 3.05247C10.2393 3.00282 10.1702 2.96558 10.0958 2.94296C10.0214 2.92033 9.94322 2.91277 9.86585 2.92071C9.78848 2.92866 9.71348 2.95196 9.64527 2.98924C9.57706 3.02652 9.51701 3.07702 9.46867 3.13778L7.2302 5.81607C7.11028 5.95956 6.88973 5.95955 6.76981 5.81606L4.53162 3.13778C4.48299 3.07789 4.4229 3.02824 4.35485 2.9917C4.2868 2.95517 4.21215 2.93248 4.13523 2.92496C4.05832 2.91744 3.98067 2.92524 3.9068 2.94789C3.83293 2.97055 3.76432 3.00762 3.70494 3.05694C3.64556 3.10627 3.5966 3.16687 3.56091 3.23522C3.52522 3.30358 3.5035 3.37832 3.49702 3.45512C3.49055 3.53191 3.49944 3.60922 3.52318 3.68256C3.54693 3.75589 3.58505 3.8238 3.63533 3.88232L6.08003 6.8077C6.17309 6.91907 6.17309 7.08109 6.08003 7.19245L3.63533 10.1178C3.58412 10.1762 3.54513 10.2442 3.52066 10.3178C3.49619 10.3915 3.48673 10.4692 3.49284 10.5465C3.49895 10.6239 3.52051 10.6992 3.55625 10.7681C3.59198 10.837 3.64118 10.898 3.70093 10.9477C3.76068 10.9973 3.82979 11.0346 3.90419 11.0572C3.97859 11.0798 4.05678 11.0874 4.13415 11.0794C4.21152 11.0715 4.28652 11.0482 4.35473 11.0109C4.42294 10.9736 4.48299 10.9231 4.53133 10.8624L6.76981 8.18407C6.88973 8.04058 7.11027 8.04058 7.23019 8.18407L9.46867 10.8624C9.51701 10.9231 9.57706 10.9736 9.64527 11.0109C9.71348 11.0482 9.78848 11.0715 9.86585 11.0794C9.94322 11.0874 10.0214 11.0798 10.0958 11.0572C10.1702 11.0346 10.2393 10.9973 10.2991 10.9477C10.3588 10.898 10.408 10.837 10.4438 10.7681C10.4795 10.6992 10.501 10.6239 10.5072 10.5465C10.5133 10.4692 10.5038 10.3915 10.4793 10.3178C10.4549 10.2442 10.4159 10.1762 10.3647 10.1178L7.91997 7.19245C7.82691 7.08109 7.82691 6.91907 7.91997 6.8077L10.3647 3.88232Z"
                      fill="#FF5757"
                    />
                  </g>
                </svg>
              )}
              {loanCustomer?.loan_status === "Defaulted" && (
                <svg
                  width="13"
                  height="15"
                  viewBox="0 0 13 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.2455 2.19481L6.99551 0.601055C6.67676 0.480638 6.32259 0.480638 6.00384 0.601055L1.75384 2.19481C1.20134 2.40731 0.833008 2.93147 0.833008 3.52647V6.85564C0.833008 10.3548 3.14926 13.6344 6.29426 14.5269C6.42884 14.5623 6.57051 14.5623 6.69801 14.5269C9.85009 13.6344 12.1663 10.3548 12.1663 6.85564V3.52647C12.1663 2.93147 11.798 2.40731 11.2455 2.19481ZM8.48301 9.47647C8.20676 9.75272 7.76051 9.75272 7.48426 9.48356L6.49967 8.50606L5.51509 9.48356C5.38265 9.616 5.20302 9.6904 5.01572 9.6904C4.82841 9.6904 4.64878 9.616 4.51634 9.48356C4.3839 9.35111 4.30949 9.17148 4.30949 8.98418C4.30949 8.79688 4.3839 8.61725 4.51634 8.48481L5.50092 7.50022L4.51634 6.51564C4.3839 6.3832 4.30949 6.20357 4.30949 6.01626C4.30949 5.82896 4.3839 5.64933 4.51634 5.51689C4.64878 5.38445 4.82841 5.31004 5.01572 5.31004C5.20302 5.31004 5.38265 5.38445 5.51509 5.51689L6.49967 6.50147L7.48426 5.51689C7.6167 5.38445 7.79633 5.31004 7.98363 5.31004C8.17093 5.31004 8.35057 5.38445 8.48301 5.51689C8.61545 5.64933 8.68986 5.82896 8.68986 6.01626C8.68986 6.20357 8.61545 6.3832 8.48301 6.51564L7.50551 7.50022L8.48301 8.47772C8.75926 8.75397 8.75926 9.20022 8.48301 9.47647Z"
                    fill="#FF3232"
                  />
                </svg>
              )}
              {loanCustomer?.loan_status === "Cleared" && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_9348_2027)">
                    <path
                      d="M6 0C6.55078 0 7.08203 0.0703125 7.59375 0.210938C8.10547 0.351562 8.58203 0.554688 9.02344 0.820312C9.46484 1.08594 9.86914 1.39844 10.2363 1.75781C10.6035 2.11719 10.918 2.52148 11.1797 2.9707C11.4414 3.41992 11.6426 3.89844 11.7832 4.40625C11.9238 4.91406 11.9961 5.44531 12 6C12 6.55078 11.9297 7.08203 11.7891 7.59375C11.6484 8.10547 11.4453 8.58203 11.1797 9.02344C10.9141 9.46484 10.6016 9.86914 10.2422 10.2363C9.88281 10.6035 9.47852 10.918 9.0293 11.1797C8.58008 11.4414 8.10156 11.6426 7.59375 11.7832C7.08594 11.9238 6.55469 11.9961 6 12C5.44922 12 4.91797 11.9297 4.40625 11.7891C3.89453 11.6484 3.41797 11.4453 2.97656 11.1797C2.53516 10.9141 2.13086 10.6016 1.76367 10.2422C1.39648 9.88281 1.08203 9.47852 0.820312 9.0293C0.558594 8.58008 0.357422 8.10156 0.216797 7.59375C0.0761719 7.08594 0.00390625 6.55469 0 6C0 5.44922 0.0703125 4.91797 0.210938 4.40625C0.351562 3.89453 0.554688 3.41797 0.820312 2.97656C1.08594 2.53516 1.39844 2.13086 1.75781 1.76367C2.11719 1.39648 2.52148 1.08203 2.9707 0.820312C3.41992 0.558594 3.89844 0.357422 4.40625 0.216797C4.91406 0.0761719 5.44531 0.00390625 6 0ZM9.5332 4.01367L8.73633 3.2168L4.875 7.07812L3.26367 5.4668L2.4668 6.26367L4.875 8.67188L9.5332 4.01367Z"
                      fill="#0C722F"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_9348_2027">
                      <rect width="12" height="12" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              )}
              {loanCustomer?.loan_status === "NPL" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19"
                  height="20"
                  viewBox="0 0 19 20"
                  fill="none"
                >
                  <path
                    d="M9.5 10.8906V6.14062ZM17.2188 10C17.2187 12.0471 16.4055 14.0104 14.958 15.458C13.5104 16.9055 11.5471 17.7188 9.5 17.7188C7.45286 17.7188 5.48957 16.9055 4.04202 15.458C2.59447 14.0104 1.78125 12.0471 1.78125 10C1.78125 7.95286 2.59447 5.98957 4.04202 4.54202C5.48957 3.09447 7.45286 2.28125 9.5 2.28125C11.5471 2.28125 13.5104 3.09447 14.958 4.54202C16.4055 5.98957 17.2188 7.95286 17.2188 10Z"
                    fill="#FF3232"
                  />
                  <path
                    d="M9.5 10.8906V6.14062M17.2188 10C17.2187 12.0471 16.4055 14.0104 14.958 15.458C13.5104 16.9055 11.5471 17.7188 9.5 17.7188C7.45286 17.7188 5.48957 16.9055 4.04202 15.458C2.59447 14.0104 1.78125 12.0471 1.78125 10C1.78125 7.95286 2.59447 5.98957 4.04202 4.54202C5.48957 3.09447 7.45286 2.28125 9.5 2.28125C11.5471 2.28125 13.5104 3.09447 14.958 4.54202C16.4055 5.98957 17.2188 7.95286 17.2188 10Z"
                    stroke="white"
                    stroke-width="1.3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.0938 13.5625C10.0938 13.72 10.0312 13.871 9.91985 13.9823C9.8085 14.0937 9.65747 14.1562 9.5 14.1562C9.34253 14.1562 9.19151 14.0937 9.08016 13.9823C8.96881 13.871 8.90625 13.72 8.90625 13.5625C8.90625 13.405 8.96881 13.254 9.08016 13.1427C9.19151 13.0313 9.34253 12.9688 9.5 12.9688C9.65747 12.9688 9.8085 13.0313 9.91985 13.1427C10.0312 13.254 10.0938 13.405 10.0938 13.5625Z"
                    fill="#FF3232"
                    stroke="white"
                  />
                </svg>
              )}
              {loanCustomer?.loan_status === "Pending" && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                    fill="#FFCB35"
                  />
                </svg>
              )}
              {loanCustomer?.loan_status === "Pending Approval" && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                    fill="#FFCB35"
                  />
                </svg>
              )}
              {loanCustomer?.loan_status === "Approved" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <g clip-path="url(#clip0_8496_1672)">
                    <path
                      d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
                      fill="#0C722F"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_8496_1672">
                      <rect width="12" height="12" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              )}
              <p className="text-[#074073] text-[12px]">
                {loanCustomer?.loan_status}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2">
              <div
                className="flex items-center h-[30px] px-[20px] text-[16px] font-normal rounded-[30px] text-white"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.31)" }}
              >
                {loanType === "Guarantor_loan"
                  ? "Normal loan"
                  : loanType?.replace("_", " ")}
              </div>
              <p className="text-center !text-[24px] text-white">#{loanCode}</p>
            </div>
          </div>
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col w-[33%]">
              <p className="text-white !text-[16px]">Interest</p>
              <p className="text-white !text-[16px] font-bold">
                {loanCustomer?.loan_interest_per != null
                  ? `${loanCustomer?.loan_interest_per}% ${
                      loanCustomer?.interest_key || ""
                    }`
                  : "Not available"}
              </p>
            </div>
            <div className="flex flex-col items-center w-[33%]">
              <p className="text-white !text-[14px]">Amount</p>
              <div className="flex items-center gap-[10px]">
                <p className="text-white !text-[16px] font-bold">KES</p>
                <p className="text-white !text-[32px] font-bold">
                  {Number(loanCustomer?.loan_amount || "0.00")?.toLocaleString(
                    "en-US",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-col text-right w-[33%]">
              <p className="text-white !text-[14px]">Duration</p>
              <p className="text-white !text-[16px] font-bold">
                {loanCustomer?.loan_period != null
                  ? `${Number(loanCustomer?.loan_period)?.toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      },
                    )} ${Number(loanCustomer?.loan_period) === 1 ? "Month" : "Months"}`
                  : "Not available"}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col w-[33%]">
              <p className="text-white !text-[16px]">Disbursement</p>
              <p className="text-white !text-[16px] font-bold">
                {loanCustomer?.loan_disbursement_date === "N/A"
                  ? "N/A"
                  : loanCustomer?.loan_disbursement_date != null
                    ? convertLoanDates(loanCustomer?.loan_disbursement_date)
                    : "Not available"}
              </p>
            </div>
            <div className="flex flex-col items-center w-[33%]">
              <p className="text-white !text-[14px]">Loan Balance</p>
              <div className="flex items-center gap-[10px]">
                <p className="text-white !text-[16px] font-bold">KES</p>
                <p className="text-white !text-[32px] font-bold">
                  {Number(loanCustomer?.loan_balance || "0.00").toLocaleString(
                    "en-US",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-col text-right w-[33%]">
              <p className="text-white !text-[14px]">Maturity</p>
              <p className="text-white !text-[16px] font-bold">
                {loanCustomer?.loan_due_date === "N/A"
                  ? "N/A"
                  : loanCustomer?.loan_due_date != null
                    ? convertLoanDates(loanCustomer?.loan_due_date)
                    : "Not available"}
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center w-full">
            {(((loanType === "Guarantor_loan" ||
              loanType === "Development_loan" ||
              loanType === "Jijenge_loan") &&
              loanCustomer?.loan_status === "Active") ||
              (loanType === "Emergency_loan" &&
                loanCustomer?.loan_status === "Active")) && (
              <div className="flex flex-col items-start w-[32%] self-start">
                <p className="text-white !text-[14px]">Processing Fees</p>
                <div className="flex items-center gap-[10px]">
                  <p className="text-white !text-[16px] font-bold">KES</p>
                  <p className="text-white !text-[24px] font-bold">
                    {Number(
                      loanCustomer?.loan_processing_fee || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-center items-center flex-1 gap-4">
              <PermissionGate required={[PERMISSIONS?.LN?.APPROVE_REJECT]}>
                {(loanType === "Guarantor_loan" ||
                  loanType === "Development_loan" ||
                  loanType === "Jijenge_loan") &&
                  progress < 3 &&
                  !hasApproved &&
                  loanCustomer?.loan_status === "Pending Approval" && (
                    <button
                      onClick={openDetailsPageOpen}
                      className="h-[36px] px-[20px] text-[12px] font-normal rounded-[10px] border border-[#F0FFFE] text-white"
                      style={{ backgroundColor: "rgba(255, 255, 255, 0.31)" }}
                    >
                      Approve or Decline Loan
                    </button>
                  )}
              </PermissionGate>
              <PermissionGate required={[PERMISSIONS?.LN?.DISBURSE]}>
                {(([
                  "Guarantor_loan",
                  "Development_loan",
                  "Jijenge_loan",
                ]?.includes(loanType) &&
                  loanCustomer?.loan_status === "Approved") ||
                  (loanType === "Emergency_loan" &&
                    loanCustomer?.loan_status === "Pending") ||
                  (loanType === "Business_loan" &&
                    loanCustomer?.loan_status === "Pending")) && (
                  <button
                    onClick={openPrepareLoanDisbursementModalOpen}
                    className="h-[36px] w-[200px] px-[20px] text-[12px] font-normal rounded-[10px] border border-[#F0FFFE] text-white"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.31)" }}
                  >
                    Disburse Loan
                  </button>
                )}
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white">
        {loanType === "Business_loan" && (
          <div className="flex bg-[#BFE0FD] items-center justify-between mx-4 py-4 mt-5 px-5 rounded-[10px]">
            <div className="text-[#000000] font-[300] text-[12px]">
              Financial statement analysis is a mandatory requirement for this
              loan. The applicant’s submitted Bank or M-PESA statement must be
              <br />
              analysed to evaluate financial stability and repayment capacity
              prior to further consideration of the application.
            </div>
            <button
              onClick={() => setActivePage("loanAppraisal")}
              type="button"
              className="text-[#BFE0FD] bg-[#074073] font-normal flex items-center justify-center rounded-[5px] text-[14px] px-4 py-2 text-center"
            >
              Go To Loan Appraisal
            </button>
          </div>
        )}

        <div className="flex items-start justify-between px-4 mt-2 mb-8 gap-3">
          <div
            className="h-[537px] mt-2 w-[28%] flex flex-col gap-[40px] p-[20px]"
            style={{ border: "0.5px solid #B5B5B5" }}
          >
            <div
              onClick={() => handleTabChange("customer")}
              style={containerStyle}
              className={
                activePage === "customer"
                  ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                  : "bg-white"
              }
            >
              <div
                className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                  activePage !== "customer" ? "bg-[#EDEDED]" : "bg-transparent"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M8.25 6.75C8.25 7.995 7.245 9 6 9C4.755 9 3.75 7.995 3.75 6.75C3.75 5.505 4.755 4.5 6 4.5C7.245 4.5 8.25 5.505 8.25 6.75ZM10.5 15H1.5V13.5C1.5 11.8425 3.5175 10.5 6 10.5C8.4825 10.5 10.5 11.8425 10.5 13.5M5.25 6.75C5.25 7.1625 5.5875 7.5 6 7.5C6.4125 7.5 6.75 7.1625 6.75 6.75C6.75 6.3375 6.4125 6 6 6C5.5875 6 5.25 6.3375 5.25 6.75ZM3 13.5H9C9 12.675 7.6575 12 6 12C4.3425 12 3 12.675 3 13.5ZM16.5 9V10.5H9.75V9M16.5 6V7.5H9.75V6M16.5 3V4.5H9.75V3H16.5Z"
                    fill="#074073"
                  />
                </svg>
              </div>
              <p className="text-[#074073] text-[16px]">Member details</p>
            </div>
            {loanType === "Business_loan" && (
              <div
                onClick={() => handleTabChange("loanAppraisal")}
                style={containerStyle}
                className={
                  activePage === "loanAppraisal"
                    ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                    : "bg-white"
                }
              >
                <div
                  className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                    activePage !== "loanAppraisal"
                      ? "bg-[#EDEDED]"
                      : "bg-transparent"
                  }`}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.371 20.827L21.211 18.031L20.585 17.404L18.371 19.587L17.416 18.612L16.789 19.244L18.371 20.827ZM7.77 9.731H18.232V8.731H7.769L7.77 9.731ZM19 23.116C17.886 23.116 16.941 22.7277 16.165 21.951C15.3883 21.175 15 20.23 15 19.116C15 18.002 15.3883 17.0567 16.165 16.28C16.9417 15.5033 17.8867 15.1153 19 15.116C20.1133 15.1167 21.0587 15.5047 21.836 16.28C22.6133 17.0553 23.0013 18.0007 23 19.116C23 20.2293 22.612 21.1743 21.836 21.951C21.0587 22.7277 20.1133 23.116 19 23.116ZM5 21.769V6.616C5 6.168 5.15733 5.78667 5.472 5.472C5.78667 5.15733 6.168 5 6.616 5H19.385C19.8317 5 20.2127 5.15733 20.528 5.472C20.8427 5.78667 21 6.168 21 6.616V12.56C20.8373 12.5 20.676 12.4487 20.516 12.406C20.3553 12.3633 20.1833 12.33 20 12.306V6.616C20 6.462 19.936 6.32067 19.808 6.192C19.68 6.06333 19.5387 5.99933 19.384 6H6.616C6.462 6 6.32067 6.064 6.192 6.192C6.06333 6.32 5.99933 6.46133 6 6.616V20.05H12.344C12.3893 20.3233 12.448 20.5907 12.52 20.852C12.5927 21.1133 12.6937 21.3627 12.823 21.6L12.789 21.634L11.654 20.808L10.308 21.769L8.962 20.808L7.616 21.769L6.269 20.808L5 21.769ZM7.77 17.269H12.479C12.5163 17.0857 12.5623 16.914 12.617 16.754C12.6717 16.594 12.7357 16.4323 12.809 16.269H7.77V17.269ZM7.77 13.5H15.08C15.4067 13.242 15.7567 13.027 16.13 12.855C16.5033 12.6823 16.9023 12.564 17.327 12.5H7.769L7.77 13.5Z"
                      fill="#074073"
                    />
                  </svg>
                </div>
                <p className="text-[#074073] text-[16px]">Loan Appraisal</p>
              </div>
            )}
            {loanStatus && !hiddenStatuses?.includes(loanStatus) && (
              <div
                onClick={() => handleTabChange("loanBreakdown")}
                style={containerStyle}
                className={
                  activePage === "loanBreakdown"
                    ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                    : "bg-white"
                }
              >
                <div
                  className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                    activePage !== "loanBreakdown"
                      ? "bg-[#EDEDED]"
                      : "bg-transparent"
                  }`}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M2.5 0.5H12.5C13.0304 0.5 13.5391 0.710714 13.9142 1.08579C14.2893 1.46086 14.5 1.96957 14.5 2.5V12.5C14.5 13.0304 14.2893 13.5391 13.9142 13.9142C13.5391 14.2893 13.0304 14.5 12.5 14.5H2.5C1.96957 14.5 1.46086 14.2893 1.08579 13.9142C0.710714 13.5391 0.5 13.0304 0.5 12.5V2.5C0.5 1.96957 0.710714 1.46086 1.08579 1.08579C1.46086 0.710714 1.96957 0.5 2.5 0.5Z"
                      stroke="#074073"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7.5 8.5C6.94772 8.5 6.5 8.05228 6.5 7.5C6.5 6.94772 6.94772 6.5 7.5 6.5C8.05228 6.5 8.5 6.94772 8.5 7.5C8.5 8.05228 8.05228 8.5 7.5 8.5Z"
                      fill="#074073"
                    />
                    <path
                      d="M3.5 8.5C4.05228 8.5 4.5 8.05228 4.5 7.5C4.5 6.94772 4.05228 6.5 3.5 6.5C2.94772 6.5 2.5 6.94772 2.5 7.5C2.5 8.05228 2.94772 8.5 3.5 8.5Z"
                      fill="#074073"
                    />
                    <path
                      d="M11.5 8.5C12.0523 8.5 12.5 8.05228 12.5 7.5C12.5 6.94772 12.0523 6.5 11.5 6.5C10.9477 6.5 10.5 6.94772 10.5 7.5C10.5 8.05228 10.9477 8.5 11.5 8.5Z"
                      fill="#074073"
                    />
                  </svg>
                </div>
                <p className="text-[#074073] text-[16px]">Loan Breakdown</p>
              </div>
            )}
            <div
              onClick={() => handleTabChange("guarantorship")}
              style={containerStyle}
              className={
                activePage === "guarantorship"
                  ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                  : "bg-white"
              }
            >
              <div
                className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                  activePage !== "guarantorship"
                    ? "bg-[#EDEDED]"
                    : "bg-transparent"
                }`}
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.9093 16.0873C16.7389 14.1146 14.9352 12.7001 12.8303 12.0295C13.8715 11.4253 14.6804 10.5045 15.1329 9.40862C15.5854 8.31276 15.6563 7.10239 15.3349 5.9634C15.0135 4.82441 14.3174 3.81978 13.3537 3.10378C12.3899 2.38778 11.2117 2 10 2C8.7883 2 7.6101 2.38778 6.64633 3.10378C5.68257 3.81978 4.98653 4.82441 4.6651 5.9634C4.34368 7.10239 4.41464 8.31276 4.8671 9.40862C5.31955 10.5045 6.12848 11.4253 7.16965 12.0295C5.06476 12.6993 3.26112 14.1138 2.09071 16.0873C2.04779 16.1555 2.01932 16.2314 2.00698 16.3106C1.99464 16.3897 1.99869 16.4704 2.01887 16.548C2.03906 16.6256 2.07498 16.6984 2.12452 16.7623C2.17405 16.8261 2.2362 16.8796 2.30729 16.9196C2.37838 16.9597 2.45698 16.9854 2.53844 16.9954C2.6199 17.0053 2.70258 16.9992 2.7816 16.9775C2.86062 16.9558 2.93437 16.9189 2.99852 16.8689C3.06266 16.819 3.11589 16.757 3.15507 16.6866C4.6029 14.2472 7.16197 12.7907 10 12.7907C12.838 12.7907 15.3971 14.2472 16.8449 16.6866C16.8841 16.757 16.9373 16.819 17.0015 16.8689C17.0656 16.9189 17.1394 16.9558 17.2184 16.9775C17.2974 16.9992 17.3801 17.0053 17.4616 16.9954C17.543 16.9854 17.6216 16.9597 17.6927 16.9196C17.7638 16.8796 17.8259 16.8261 17.8755 16.7623C17.925 16.6984 17.9609 16.6256 17.9811 16.548C18.0013 16.4704 18.0054 16.3897 17.993 16.3106C17.9807 16.2314 17.9522 16.1555 17.9093 16.0873ZM5.69646 7.39638C5.69646 6.56657 5.94886 5.7554 6.42174 5.06543C6.89462 4.37547 7.56674 3.83771 8.35311 3.52015C9.13948 3.2026 10.0048 3.11951 10.8396 3.2814C11.6744 3.44329 12.4412 3.84288 13.0431 4.42965C13.6449 5.01641 14.0548 5.764 14.2208 6.57786C14.3869 7.39173 14.3017 8.23533 13.976 9.00197C13.6502 9.76862 13.0986 10.4239 12.3909 10.8849C11.6832 11.3459 10.8512 11.592 10 11.592C8.85901 11.5908 7.76509 11.1484 6.95829 10.3618C6.15148 9.57524 5.69768 8.50876 5.69646 7.39638Z"
                    fill="#074073"
                  />
                  <path
                    d="M11.25 15.25V14.75C11.25 14.4185 11.1183 14.1005 10.8839 13.8661C10.6495 13.6317 10.3315 13.5 10 13.5C9.66848 13.5 9.35054 13.6317 9.11612 13.8661C8.8817 14.1005 8.75 14.4185 8.75 14.75V15.25C8.55109 15.25 8.36032 15.329 8.21967 15.4697C8.07902 15.6103 8 15.8011 8 16V17.75C8 17.9489 8.07902 18.1397 8.21967 18.2803C8.36032 18.421 8.55109 18.5 8.75 18.5H11.25C11.4489 18.5 11.6397 18.421 11.7803 18.2803C11.921 18.1397 12 17.9489 12 17.75V16C12 15.8011 11.921 15.6103 11.7803 15.4697C11.6397 15.329 11.4489 15.25 11.25 15.25ZM9.25 14.75C9.25 14.5511 9.32902 14.3603 9.46967 14.2197C9.61032 14.079 9.80109 14 10 14C10.1989 14 10.3897 14.079 10.5303 14.2197C10.671 14.3603 10.75 14.5511 10.75 14.75V15.25H9.25V14.75ZM11.5 17.75C11.5 17.8163 11.4737 17.8799 11.4268 17.9268C11.3799 17.9737 11.3163 18 11.25 18H8.75C8.6837 18 8.62011 17.9737 8.57322 17.9268C8.52634 17.8799 8.5 17.8163 8.5 17.75V16C8.5 15.9337 8.52634 15.8701 8.57322 15.8232C8.62011 15.7763 8.6837 15.75 8.75 15.75H11.25C11.3163 15.75 11.3799 15.7763 11.4268 15.8232C11.4737 15.8701 11.5 15.9337 11.5 16V17.75Z"
                    fill="#074073"
                  />
                </svg>
              </div>
              <p className="text-[#074073] text-[16px]">Guarantorship</p>
            </div>

            <div
              onClick={() => handleTabChange("audit")}
              style={containerStyle}
              className={
                activePage === "audit"
                  ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                  : "bg-white"
              }
            >
              <div
                className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                  activePage !== "audit" ? "bg-[#EDEDED]" : "bg-transparent"
                }`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.675 20.337L20.221 19.79L18.385 17.954V15.23H17.615V18.276L19.675 20.337ZM5.615 20C5.16833 20 4.78733 19.8427 4.472 19.528C4.15733 19.2133 4 18.8323 4 18.385V5.615C4 5.16833 4.15733 4.78733 4.472 4.472C4.78667 4.15733 5.16767 4 5.615 4H18.385C18.8317 4 19.2127 4.15733 19.528 4.472C19.8427 4.78667 20 5.16767 20 5.615V11.565C19.8247 11.505 19.6567 11.456 19.496 11.418C19.336 11.3807 19.1707 11.3433 19 11.306V5.616C19 5.462 18.936 5.32067 18.808 5.192C18.6793 5.064 18.5383 5 18.385 5H5.615C5.46167 5 5.32067 5.064 5.192 5.192C5.064 5.32067 5 5.46167 5 5.615V18.385C5 18.5383 5.064 18.6793 5.192 18.808C5.32067 18.936 5.46167 19 5.615 19H11.281C11.305 19.1867 11.336 19.3603 11.374 19.521C11.412 19.681 11.461 19.8407 11.521 20H5.615ZM5 18V19V5V11.306V11.231V18ZM7.5 16.27H11.46C11.4967 16.0987 11.5467 15.933 11.61 15.773L11.81 15.269H7.5V16.27ZM7.5 12.5H14.08C14.4087 12.2693 14.732 12.0737 15.05 11.913C15.3693 11.753 15.7117 11.6277 16.077 11.537V11.5H7.5V12.5ZM7.5 8.73H16.5V7.73H7.5V8.73ZM18 22.116C16.886 22.116 15.9407 21.728 15.164 20.952C14.3873 20.176 13.9993 19.2303 14 18.115C14 17.0017 14.388 16.0567 15.164 15.28C15.94 14.5033 16.8853 14.115 18 14.115C19.114 14.115 20.0593 14.5033 20.836 15.28C21.6127 16.0567 22.0007 17.0017 22 18.115C22 19.2297 21.612 20.175 20.836 20.951C20.0587 21.727 19.1133 22.116 18 22.116Z"
                    fill="#074073"
                  />
                </svg>
              </div>
              <p className="text-[#074073] text-[16px]">Loan Audit</p>
            </div>
            <div
              onClick={() => handleTabChange("history")}
              style={containerStyle}
              className={
                activePage === "history"
                  ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                  : "bg-white"
              }
            >
              <div
                className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                  activePage !== "history" ? "bg-[#EDEDED]" : "bg-transparent"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11.9623 20C9.93094 20 8.16061 19.3367 6.65127 18.01C5.14194 16.6847 4.26827 15.0147 4.03027 13H5.04127C5.31327 14.72 6.09694 16.15 7.39227 17.29C8.68894 18.43 10.2123 19 11.9623 19C13.9123 19 15.5663 18.321 16.9243 16.963C18.2823 15.605 18.9616 13.9507 18.9623 12C18.9623 10.05 18.2829 8.39567 16.9243 7.037C15.5656 5.67833 13.9116 4.99933 11.9623 5C10.9276 5 9.95494 5.21867 9.04427 5.656C8.13427 6.09333 7.33094 6.69533 6.63427 7.462H9.11527V8.462H4.96227V4.308H5.96227V6.696C6.73561 5.84733 7.64161 5.186 8.68027 4.712C9.71894 4.23733 10.8129 4 11.9623 4C13.0709 4 14.1103 4.20867 15.0803 4.626C16.0503 5.04333 16.8976 5.61433 17.6223 6.339C18.3469 7.06367 18.9183 7.91133 19.3363 8.882C19.7536 9.852 19.9623 10.8913 19.9623 12C19.9623 13.1087 19.7536 14.148 19.3363 15.118C18.9189 16.088 18.3476 16.9357 17.6223 17.661C16.8976 18.3857 16.0503 18.9567 15.0803 19.374C14.1103 19.7913 13.0709 20 11.9623 20ZM15.1653 15.854L11.5193 12.208V7H12.5193V11.792L15.8733 15.146L15.1653 15.854Z"
                    fill="#074073"
                  />
                </svg>
              </div>
              <p className="text-[#074073] text-[16px]">Payment history</p>
            </div>

            <div
              onClick={() => handleTabChange("actions")}
              style={containerStyle}
              className={
                activePage === "actions"
                  ? "bg-[#BDE0FF] rounded-[30px] py-[10px] w-[70%]"
                  : "bg-white"
              }
            >
              <div
                className={`h-[24px] w-[24px] flex items-center justify-center mr-3 ${
                  activePage !== "actions" ? "bg-[#EDEDED]" : "bg-transparent"
                }`}
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M5.5 3.5H15.5C16.0304 3.5 16.5391 3.71071 16.9142 4.08579C17.2893 4.46086 17.5 4.96957 17.5 5.5V15.5C17.5 16.0304 17.2893 16.5391 16.9142 16.9142C16.5391 17.2893 16.0304 17.5 15.5 17.5H5.5C4.96957 17.5 4.46086 17.2893 4.08579 16.9142C3.71071 16.5391 3.5 16.0304 3.5 15.5V5.5C3.5 4.96957 3.71071 4.46086 4.08579 4.08579C4.46086 3.71071 4.96957 3.5 5.5 3.5Z"
                    stroke="#074073"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.5 11.5C9.94772 11.5 9.5 11.0523 9.5 10.5C9.5 9.94772 9.94772 9.5 10.5 9.5C11.0523 9.5 11.5 9.94772 11.5 10.5C11.5 11.0523 11.0523 11.5 10.5 11.5Z"
                    fill="#074073"
                  />
                  <path
                    d="M6.5 11.5C7.05228 11.5 7.5 11.0523 7.5 10.5C7.5 9.94772 7.05228 9.5 6.5 9.5C5.94772 9.5 5.5 9.94772 5.5 10.5C5.5 11.0523 5.94772 11.5 6.5 11.5Z"
                    fill="#074073"
                  />
                  <path
                    d="M14.5 11.5C15.0523 11.5 15.5 11.0523 15.5 10.5C15.5 9.94772 15.0523 9.5 14.5 9.5C13.9477 9.5 13.5 9.94772 13.5 10.5C13.5 11.0523 13.9477 11.5 14.5 11.5Z"
                    fill="#074073"
                  />
                </svg>
              </div>
              <p className="text-[#074073] text-[16px]">Actions</p>
            </div>
          </div>

          <div className="flex flex-col w-[72%] mt-2">
            {["guarantor_loan", "development_loan", "jijenge_loan"]?.includes(
              loanType?.toLowerCase(),
            ) &&
              ["pending approval", "approved"]?.includes(
                loanCustomer?.loan_status?.toLowerCase(),
              ) && (
                <div className="bg-[#F0FFFE]  rounded-[5px] mb-5 px-5 py-2 border border-[#F0FFFE]">
                  <div className="">
                    {progress < 3 ? (
                      <div>
                        <p className="text-[#074073] text-[14px] font-[500]">
                          Loan Pending Approval
                        </p>
                        <p className="text-[#000000] text-[12px] font-normal">
                          The loan is currently pending approval. At least one
                          member of the loan committee must approve it before it
                          can proceed to the
                          <br />
                          next stage.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-[#074073] text-[14px] font-[500]">
                          Approval Complete
                        </p>
                        <p className="text-[#000000] text-[12px] font-normal">
                          The loan has been approved. All required approvals are
                          complete, and the application is now ready for
                          <br />
                          disbursement.
                        </p>
                      </div>
                    )}
                  </div>

                  <p className="text-[12px] font-[400] text-[#383838] opacity-70">
                    Approval progress
                  </p>
                  <div className="flex items-center mt-2">
                    <div className="w-[352px] bg-[#C6DEA5] rounded-full h-[12px] relative overflow-hidden">
                      <div
                        className="h-[12px] bg-[#074073] transition-all duration-500"
                        style={{ width: getPercentage() }}
                      ></div>
                    </div>
                    <span className="ml-1.5 text-[#074073] text-[12px] font-[600]">
                      {getPercentage()} {getProgressLabel()}
                    </span>
                  </div>
                </div>
              )}

            <div
              className="h-[537px] bg-[#FFFFFF] flex flex-col gap-[40px] w-[100%] overflow-y-auto p-[15px]"
              style={{ border: "0.5px solid #B5B5B5" }}
            >
              {activePage === "customer" && (
                <div className="flex flex-col gap-[40px]">
                  <p className="text-[15px] text-[#000]">
                    For more Member details click on the Member ID{" "}
                  </p>
                  {loanCustomer?.customer?.public_id ? (
                    <>
                      <div className="flex flex-col">
                        <p className="text-[#000000] text-[16px] font-[500]">
                          Member ID
                        </p>

                        <PermissionGate
                          required={[
                            PERMISSIONS?.MB?.VIEW_DETAILS,
                            PERMISSIONS?.MB?.EDIT_INFO,
                          ]}
                        >
                          <p
                            onClick={() =>
                              navigateToCustomer(loanCustomer?.customer?.id)
                            }
                            className="text-[#037BD1] text-[15px] cursor-pointer"
                          >
                            #{loanCustomer?.customer?.public_id}
                          </p>
                        </PermissionGate>

                        <PermissionGate
                          required={[
                            PERMISSIONS?.MB?.VIEW_DETAILS,
                            PERMISSIONS?.MB?.EDIT_INFO,
                          ]}
                          fallback={
                            <p className="text-[#037BD1] text-[15px] cursor-not-allowed">
                              #{loanCustomer?.customer?.public_id}
                            </p>
                          }
                        />
                      </div>
                      <div className="flex items-center w-full">
                        <div className="flex flex-col w-[40%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            Firstname
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {loanCustomer?.customer?.firstname || "N/A"}
                          </p>
                        </div>
                        <div className="flex flex-col w-[60%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            Middlename
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {loanCustomer?.customer?.middlename
                              ? loanCustomer?.customer?.middlename
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center w-full">
                        <div className="flex flex-col w-[40%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            Lastname
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {loanCustomer?.customer?.lastname || "N/A"}
                          </p>
                        </div>
                        <div className="flex flex-col w-[60%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            Date of Birth
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {convertDateOfBirth(loanCustomer?.customer?.dob) ||
                              "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center w-full">
                        <div className="flex flex-col w-[40%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            Phone number
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {loanCustomer?.customer?.mobileno || "N/A"}
                          </p>
                        </div>
                        <div className="flex flex-col w-[60%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            Email
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {loanCustomer?.customer?.email || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center w-full">
                        <div className="flex flex-col w-[40%]">
                          <p className="text-[#000000] text-[16px] font-[500]">
                            KRA PIN
                          </p>
                          <p className="text-[#4F4F4F] text-[15px]">
                            {loanCustomer?.customer?.kraPin || "N/A"}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-[#074073] text-[15px]">
                      Member details not available.
                    </p>
                  )}
                </div>
              )}

              {activePage === "loanAppraisal" && (
                <div>
                  <div className="flex flex-col justify-center mb-5">
                    <p className="text-[15px] font-[600] text-[#000000]">
                      Loan Appraisal
                    </p>
                    <p className="text-[#000000] text-[15px] font-[300]">
                      Add, review, view, and download financial statements for
                      loan assessment.
                    </p>
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      onClick={openUploadFinancialStatementOpen}
                      type="button"
                      className="text-[#BFE0FD] bg-[#074073] font-normal flex items-center justify-center rounded-[5px] text-[14px] px-2 py-2 text-center"
                    >
                      Upload & Analyse a New Financial Statement
                    </button>
                  </div>
                  <div className="h-[44px] w-full text-[14px] px-[20px] flex items-center mt-[10px] bg-[#E3F8FF]">
                    <p className="w-[25%] !m-0 !p-0">DATE UPLOADED</p>
                    <p className="w-[35%] !m-0 !p-0">DOCUMENT</p>
                    <p className="w-[30%] !m-0 !p-0">STATEMENT TYPE</p>
                    <p className="w-[30%] !m-0 !p-0">STATUS</p>
                    <p className="w-[30%]  text-center !m-0 !p-0">ACTION</p>
                  </div>
                  {statements?.length > 0 ? (
                    statements?.map((statement) => (
                      <div
                        className="h-[44px] w-full px-[20px] flex items-center mt-[10px]"
                        key={statement?.id}
                      >
                        {/* DATE UPLOADED */}
                        <p className="w-[25%]">
                          {
                            new Date(statement?.createdAt)
                              ?.toISOString()
                              ?.split("T")[0]
                          }
                        </p>

                        {/* DOCUMENT NAME */}
                        <p className="w-[35%] text-[#316CE9] cursor-pointer underline">
                          <a
                            href={statement?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {statement?.type === "mpesa"
                              ? "MPESA_Statement.pdf"
                              : statement?.type === "bank"
                                ? "Bank_Statement.pdf"
                                : statement?.originalName}
                          </a>
                        </p>

                        {/* STATEMENT TYPE */}
                        <p className="w-[30%]">
                          {capitalize(statement?.type)} Statement
                        </p>

                        {/* STATUS */}
                        <div className="flex items-center gap-3 w-[30%]">
                          {statement?.status === "Reviewed" && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <g clip-path="url(#clip0_7769_947)">
                                <path
                                  d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
                                  fill="#0C722F"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_7769_947">
                                  <rect width="12" height="12" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                          )}
                          {statement?.status === "Pending Review" && (
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                                fill="#FFCB35"
                              />
                            </svg>
                          )}
                          <p className="">{statement?.status}</p>
                        </div>
                        {/* ACTION */}
                        <div className="w-[30%] flex items-center justify-center cursor-pointer">
                          {statement?.status === "Reviewed" && (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="cursor-pointer"
                              onClick={() => handleStatementClick(statement)}
                            >
                              <path
                                d="M19.3377 9.7875C18.6026 7.88603 17.3265 6.24164 15.667 5.05755C14.0075 3.87347 12.0374 3.20161 10.0002 3.125C7.96305 3.20161 5.99296 3.87347 4.33347 5.05755C2.67398 6.24164 1.39782 7.88603 0.662722 9.7875C0.613077 9.92482 0.613077 10.0752 0.662722 10.2125C1.39782 12.114 2.67398 13.7584 4.33347 14.9424C5.99296 16.1265 7.96305 16.7984 10.0002 16.875C12.0374 16.7984 14.0075 16.1265 15.667 14.9424C17.3265 13.7584 18.6026 12.114 19.3377 10.2125C19.3874 10.0752 19.3874 9.92482 19.3377 9.7875ZM10.0002 15.625C6.68772 15.625 3.18772 13.1687 1.91897 10C3.18772 6.83125 6.68772 4.375 10.0002 4.375C13.3127 4.375 16.8127 6.83125 18.0815 10C16.8127 13.1687 13.3127 15.625 10.0002 15.625Z"
                                fill="black"
                              />
                              <path
                                d="M10 6.25C9.25832 6.25 8.5333 6.46993 7.91661 6.88199C7.29993 7.29404 6.81928 7.87971 6.53545 8.56494C6.25162 9.25016 6.17736 10.0042 6.32206 10.7316C6.46675 11.459 6.8239 12.1272 7.34835 12.6517C7.8728 13.1761 8.54098 13.5333 9.26841 13.6779C9.99584 13.8226 10.7498 13.7484 11.4351 13.4645C12.1203 13.1807 12.706 12.7001 13.118 12.0834C13.5301 11.4667 13.75 10.7417 13.75 10C13.75 9.00544 13.3549 8.05161 12.6517 7.34835C11.9484 6.64509 10.9946 6.25 10 6.25ZM10 12.5C9.50555 12.5 9.0222 12.3534 8.61108 12.0787C8.19995 11.804 7.87952 11.4135 7.6903 10.9567C7.50108 10.4999 7.45158 9.99723 7.54804 9.51227C7.6445 9.02732 7.8826 8.58186 8.23223 8.23223C8.58187 7.8826 9.02732 7.6445 9.51228 7.54804C9.99723 7.45157 10.4999 7.50108 10.9567 7.6903C11.4135 7.87952 11.804 8.19995 12.0787 8.61107C12.3534 9.0222 12.5 9.50555 12.5 10C12.5 10.663 12.2366 11.2989 11.7678 11.7678C11.2989 12.2366 10.663 12.5 10 12.5Z"
                                fill="black"
                              />
                            </svg>
                          )}
                          {statement?.status === "Pending Review" && (
                            <button
                              type="button"
                              onClick={() => handleStatementClick(statement)}
                              className="text-[#FFFFFF] bg-[#0C722F] font-normal flex items-center justify-center rounded-[5px] text-[14px] px-1.5 py-1 text-center"
                            >
                              ANALYSE
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-700 mt-4 text-lg font-bold flex justify-center items-center h-32">
                      You have no statements.
                    </p>
                  )}
                </div>
              )}

              {activePage === "loanBreakdown" && (
                <div>
                  <p className="text-[12px] font-[300] text-[#000000]">
                    A detailed view of the loan balance, showing how the total
                    amount is calculated from principal and accrued interest.
                  </p>
                  <div className="bg-[#FAFAFA] mt-5 rounded-[4px] w-full h-[260px] px-4 flex flex-col justify-center">
                    <div className="flex flex-col space-y-8">
                      <div className="flex items-center">
                        <div className="w-[50%]">
                          <p className="text-[#000000] font-[300] text-[14px] sm:text-[16px]">
                            Principal Balance
                          </p>
                        </div>
                        <div className="w-[45%]">
                          <p className="text-[#000000] font-[600] text-[14px] sm:text-[16px]">
                            KES{" "}
                            {Number(
                              loanCustomer?.loan_principal_balance || "0.00",
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="w-[50%]">
                          <p className="text-[#000000] font-[300] text-[14px] sm:text-[16px]">
                            Accrued Interest
                          </p>
                        </div>
                        <div className="w-[45%]">
                          <p className="text-[#000000] font-[600] text-[14px] sm:text-[16px]">
                            KES{" "}
                            {Number(
                              loanCustomer?.loan_interest_balance || "0.00",
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="w-[50%]">
                          <p className="text-[#000000] font-[300] text-[14px] sm:text-[16px]">
                            Penalties
                          </p>
                        </div>
                        <div className="w-[45%]">
                          <p className="text-[#000000] font-[600] text-[14px] sm:text-[16px]">
                            KES{" "}
                            {Number(
                              loanCustomer?.loan_penalty || "0.00",
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="w-[50%]">
                          <p className="text-[#000000] font-[600] text-[14px] sm:text-[16px]">
                            Total Loan Balance
                          </p>
                        </div>
                        <div className="w-[45%]">
                          <p className="text-[#000000] font-[600] text-[14px] sm:text-[16px]">
                            KES{" "}
                            {Number(
                              loanCustomer?.loan_balance || "0.00",
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePage === "audit" && (
                <div className="">
                  <p className="mb-[20px]">
                    The loan audit module tracks the loan journey from
                    application to repayment.
                  </p>
                  {(loanType === "Guarantor_loan" ||
                    loanType === "Emergency_loan" ||
                    loanType === "Development_loan" ||
                    loanType === "Jijenge_loan") &&
                  !["Pending Approval", "Pending", "Rejected"]?.includes(
                    loanCustomer?.loan_status,
                  ) ? (
                    loanSchedule ? (
                      <>
                        <div className="flex w-full h-[420px] overflow-y-auto">
                          <LoanProgress
                            loanSummary={loanSchedule}
                            requestApprovals={requestApprovals}
                            openApproversDetailsOpen={openApproversDetailsOpen}
                          />
                        </div>
                        <div>
                          <p
                            className="text-[#074073] text-[14px] underline cursor-pointer"
                            onClick={() => handleViewMore(loanSchedule)}
                            style={style}
                          >
                            View more...
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="text-[#074073] font-bold text-center">
                        No loan schedule data available.
                      </p>
                    )
                  ) : loanType !== "Guarantor_loan" &&
                    loanType !== "Emergency_loan" &&
                    loanType !== "Development_loan" &&
                    loanType !== "Jijenge_loan" &&
                    loanSchedule ? (
                    <>
                      <div className="flex w-full h-[420px] overflow-y-auto">
                        <LoanProgress
                          loanSummary={loanSchedule}
                          requestApprovals={requestApprovals}
                        />
                      </div>
                      <div>
                        <p
                          className="text-[#074073] text-[14px] underline cursor-pointer"
                          onClick={() => handleViewMore(loanSchedule)}
                          style={style}
                        >
                          View more...
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-[#074073] font-bold text-center">
                      No loan schedule data available.
                    </p>
                  )}
                </div>
              )}
              {activePage === "history" && (
                <div>
                  <div className="flex justify-between items-center mb-5">
                    <p>Payment History</p>
                    {!(
                      (loanType === "Guarantor_loan" ||
                        loanType === "Emergency_loan" ||
                        loanType === "Development_loan" ||
                        loanType === "Jijenge_loan") &&
                      [
                        "Pending Approval",
                        "Pending",
                        "Approved",
                        "Rejected",
                      ]?.includes(loanCustomer?.loan_status)
                    ) && (
                      <div className="flex items-center justify-end">
                        <PermissionGate
                          required={[PERMISSIONS?.LN?.ADD_DEPOSIT]}
                        >
                          <button
                            onClick={openLoanAddTransactionsOpen}
                            className="block text-white bg-[#074073] focus:outline-none focus:ring-[#074073] rounded-[30px] text-center w-[182px] h-[44px]"
                            type="button"
                          >
                            <div className="flex items-center justify-center gap-4">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M11 13H5V11H11V5H13V11H19V13H13V19H11V13Z"
                                  fill="white"
                                />
                              </svg>
                              <p className="text-[#FFF] text-[14px]">
                                Add Payment
                              </p>
                            </div>
                          </button>
                        </PermissionGate>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center w-full my-[10px] gap-[10px]">
                    <div className="relative w-[47%]">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="13"
                          viewBox="0 0 14 13"
                          fill="none"
                        >
                          <circle
                            cx="5.35294"
                            cy="5.35294"
                            r="4.85294"
                            stroke="#999999"
                          />
                          <line
                            x1="9.52933"
                            y1="8.82294"
                            x2="13.3529"
                            y2="12.6465"
                            stroke="#999999"
                          />
                        </svg>
                      </span>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search transaction id"
                        className="h-[30px] pl-10 border-[1px] border-[#CCCCCC] text-[14px] w-full px-[10px] focus:outline-none"
                        autoComplete="nope"
                      />
                    </div>
                    <div className="h-[30px] w-[5%] bg-[#FBF0E1] flex items-center justify-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.66699 4.5835C1.66699 4.25198 1.79869 3.93403 2.03311 3.69961C2.26753 3.46519 2.58547 3.3335 2.91699 3.3335H17.0837C17.4152 3.3335 17.7331 3.46519 17.9675 3.69961C18.202 3.93403 18.3337 4.25198 18.3337 4.5835C18.3337 4.91502 18.202 5.23296 17.9675 5.46738C17.7331 5.7018 17.4152 5.8335 17.0837 5.8335H2.91699C2.58547 5.8335 2.26753 5.7018 2.03311 5.46738C1.79869 5.23296 1.66699 4.91502 1.66699 4.5835ZM5.00033 9.5835C5.00033 9.25198 5.13202 8.93403 5.36644 8.69961C5.60086 8.46519 5.9188 8.3335 6.25033 8.3335H13.7503C14.0818 8.3335 14.3998 8.46519 14.6342 8.69961C14.8686 8.93403 15.0003 9.25198 15.0003 9.5835C15.0003 9.91502 14.8686 10.233 14.6342 10.4674C14.3998 10.7018 14.0818 10.8335 13.7503 10.8335H6.25033C5.9188 10.8335 5.60086 10.7018 5.36644 10.4674C5.13202 10.233 5.00033 9.91502 5.00033 9.5835ZM8.75033 13.3335C8.4188 13.3335 8.10086 13.4652 7.86644 13.6996C7.63202 13.934 7.50033 14.252 7.50033 14.5835C7.50033 14.915 7.63202 15.233 7.86644 15.4674C8.10086 15.7018 8.4188 15.8335 8.75033 15.8335H11.2503C11.5818 15.8335 11.8998 15.7018 12.1342 15.4674C12.3686 15.233 12.5003 14.915 12.5003 14.5835C12.5003 14.252 12.3686 13.934 12.1342 13.6996C11.8998 13.4652 11.5818 13.3335 11.2503 13.3335H8.75033Z"
                          fill="#565454"
                        />
                      </svg>
                    </div>
                    <div className="w-[47%]">
                      <Calendar
                        startDate={fromDate}
                        endDate={toDate}
                        setStartDate={setFromDate}
                        setEndDate={setToDate}
                        focusedInput={customerFocusedInput}
                        setFocusedInput={setCustomerFocusedInput}
                      />
                    </div>
                  </div>
                  <div className="h-[44px] w-full text-[14px] px-[20px] flex items-center mt-[10px] bg-[#E3F8FF]">
                    <p className="w-[20%] !m-0 !p-0">Transaction ID</p>
                    <p className="w-[20%] !m-0 !p-0">Loan Code</p>
                    <p className="w-[15%] !m-0 !p-0">Reference</p>
                    <p className="w-[20%] !m-0 !p-0">Date</p>
                    <p className="w-[20%] !m-0 !p-0">Amount</p>
                    <p className="w-[15%] !m-0 !p-0">Status</p>
                  </div>
                  {transactions?.length > 0 ? (
                    transactions?.map((transaction) => (
                      <div
                        className="h-[44px] w-full px-[20px] flex items-center mt-[10px]"
                        key={transaction?.payment_ref_no}
                      >
                        <p
                          onClick={() => handleSingleTransaction(transaction)}
                          className="w-[20%] text-[#06B217] cursor-pointer"
                        >
                          {transaction?.payment_code}
                        </p>
                        <p className="w-[20%]">{transaction?.loan_code}</p>
                        <p className="w-[15%]">{transaction?.payment_ref_no}</p>
                        <p className="w-[20%]">
                          {new Date(
                            transaction?.payment_date,
                          )?.toLocaleDateString("en-CA")}
                        </p>
                        <p className="w-[20%]">
                          KES{" "}
                          {Number(
                            transaction?.payment_amount || "0.00",
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <div className="w-[15%] flex items-center gap-[5px]">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_9612_2043)">
                              <path
                                d="M12 0C13.1016 0 14.1641 0.140625 15.1875 0.421875C16.2109 0.703125 17.1641 1.10938 18.0469 1.64062C18.9297 2.17188 19.7383 2.79688 20.4727 3.51562C21.207 4.23438 21.8359 5.04297 22.3594 5.94141C22.8828 6.83984 23.2852 7.79688 23.5664 8.8125C23.8477 9.82812 23.9922 10.8906 24 12C24 13.1016 23.8594 14.1641 23.5781 15.1875C23.2969 16.2109 22.8906 17.1641 22.3594 18.0469C21.8281 18.9297 21.2031 19.7383 20.4844 20.4727C19.7656 21.207 18.957 21.8359 18.0586 22.3594C17.1602 22.8828 16.2031 23.2852 15.1875 23.5664C14.1719 23.8477 13.1094 23.9922 12 24C10.8984 24 9.83594 23.8594 8.8125 23.5781C7.78906 23.2969 6.83594 22.8906 5.95312 22.3594C5.07031 21.8281 4.26172 21.2031 3.52734 20.4844C2.79297 19.7656 2.16406 18.957 1.64062 18.0586C1.11719 17.1602 0.714844 16.2031 0.433594 15.1875C0.152344 14.1719 0.0078125 13.1094 0 12C0 10.8984 0.140625 9.83594 0.421875 8.8125C0.703125 7.78906 1.10938 6.83594 1.64062 5.95312C2.17188 5.07031 2.79688 4.26172 3.51562 3.52734C4.23438 2.79297 5.04297 2.16406 5.94141 1.64062C6.83984 1.11719 7.79688 0.714844 8.8125 0.433594C9.82812 0.152344 10.8906 0.0078125 12 0ZM19.0664 8.02734L17.4727 6.43359L9.75 14.1562L6.52734 10.9336L4.93359 12.5273L9.75 17.3438L19.0664 8.02734Z"
                                fill="#0C722F"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_9612_2043">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <p className="text-[#074073] ml-2">Completed</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-700 mt-4 text-lg font-bold flex justify-center items-center h-32">
                      You have no transactions.
                    </p>
                  )}
                </div>
              )}

              {activePage === "guarantorship" && (
                <div className="">
                  {loanType === "Guarantor_loan" ||
                  loanType === "Development_loan" ||
                  loanType === "Jijenge_loan" ? (
                    <>
                      <p className="mb-[20px] text-[12px] font-[300] text-[#000]">
                        View and manage guarantor details and the statuses of
                        guarantorship requests sent by the borrower to potential
                        guarantors. Monitor responses and ensure compliance with
                        the loan terms.
                      </p>

                      <div className="h-[44px] w-full text-[14px] font-[400] px-[20px] flex items-center mt-[10px] bg-[#F0FFFE]">
                        <p className="w-[20%] !m-0 !p-0">Member ID</p>
                        <p className="w-[30%] !m-0 !p-0">Name</p>
                        <p className="w-[30%] !m-0 !p-0">
                          Guarantorship Amount
                        </p>
                        <p className="w-[22%] !m-0 !p-0">Status</p>
                        <p className="w-[20%] !m-0 !p-0">Actions</p>
                      </div>

                      {guarantors?.length > 0 ? (
                        guarantors?.map((guarantor) => (
                          <div
                            key={guarantor?.id}
                            className="h-[44px] w-full px-[20px] flex items-center mt-[10px]"
                          >
                            <p
                              onClick={() => {
                                setSelectedGuarantor(guarantor);
                                openGuarantorDetailsOpen();
                              }}
                              className="w-[20%] text-[#06B217] cursor-pointer font-[500] text-[14px] underline decoration-solid underline-offset-[1px] decoration-[1px]"
                            >
                              {guarantor?.memberID}
                            </p>

                            <p className="w-[30%]">
                              {guarantor?.name
                                ?.split(" ")
                                ?.map(
                                  (nam) =>
                                    nam?.charAt(0)?.toUpperCase() +
                                    nam?.slice(1)?.toLowerCase(),
                                )
                                ?.join(" ")}
                            </p>
                            <p className="w-[30%]">
                              KES{" "}
                              {Number(
                                guarantor?.currentPrincipalHeld || 0,
                              ).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>

                            <div className="w-[22%] flex items-center gap-[2px]">
                              {guarantor?.status === "accepted" && (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8 0C8.73438 0 9.44271 0.09375 10.125 0.28125C10.8073 0.46875 11.4427 0.739583 12.0312 1.09375C12.6198 1.44792 13.1589 1.86458 13.6484 2.34375C14.138 2.82292 14.5573 3.36198 14.9062 3.96094C15.2552 4.5599 15.5234 5.19792 15.7109 5.875C15.8984 6.55208 15.9948 7.26042 16 8C16 8.73438 15.9062 9.44271 15.7188 10.125C15.5312 10.8073 15.2604 11.4427 14.9062 12.0312C14.5521 12.6198 14.1354 13.1589 13.6562 13.6484C13.1771 14.138 12.638 14.5573 12.0391 14.9062C11.4401 15.2552 10.8021 15.5234 10.125 15.7109C9.44792 15.8984 8.73958 15.9948 8 16C7.26562 16 6.55729 15.9062 5.875 15.7188C5.19271 15.5312 4.55729 15.2604 3.96875 14.9062C3.38021 14.5521 2.84115 14.1354 2.35156 13.6562C1.86198 13.1771 1.44271 12.638 1.09375 12.0391C0.744792 11.4401 0.476562 10.8021 0.289062 10.125C0.101562 9.44792 0.00520833 8.73958 0 8C0 7.26562 0.09375 6.55729 0.28125 5.875C0.46875 5.19271 0.739583 4.55729 1.09375 3.96875C1.44792 3.38021 1.86458 2.84115 2.34375 2.35156C2.82292 1.86198 3.36198 1.44271 3.96094 1.09375C4.5599 0.744792 5.19792 0.476562 5.875 0.289062C6.55208 0.101562 7.26042 0.00520833 8 0ZM12.7109 5.35156L11.6484 4.28906L6.5 9.4375L4.35156 7.28906L3.28906 8.35156L6.5 11.5625L12.7109 5.35156Z"
                                    fill="#0C722F"
                                  />
                                </svg>
                              )}
                              {guarantor?.status === "pending" && (
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                                    fill="#FFCB35"
                                  />
                                </svg>
                              )}
                              {guarantor?.status === "rejected" && (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <mask
                                    id="mask0_3697_3201"
                                    maskType="luminance"
                                    maskUnits="userSpaceOnUse"
                                    x="0"
                                    y="0"
                                    width="14"
                                    height="14"
                                  >
                                    <path
                                      d="M0 0.32002C0 0.154334 0.134315 0.0200195 0.3 0.0200195H13.7C13.8657 0.0200195 14 0.154334 14 0.32002V13.6801C14 13.8458 13.8657 13.9801 13.7 13.9801H0.3C0.134315 13.9801 0 13.8458 0 13.6801V0.32002Z"
                                      fill="white"
                                    />
                                  </mask>
                                  <g mask="url(#mask0_3697_3201)">
                                    <path
                                      fill-rule="evenodd"
                                      clip-rule="evenodd"
                                      d="M14 0.32002C14 0.154334 13.8657 0.0200195 13.7 0.0200195H0.3C0.134315 0.0200195 0 0.154334 0 0.32002V13.6801C0 13.8458 0.134315 13.9801 0.3 13.9801H13.7C13.8657 13.9801 14 13.8458 14 13.6801V0.32002ZM10.3647 3.88232C10.4159 3.82394 10.4549 3.75594 10.4793 3.68232C10.5038 3.6087 10.5133 3.53094 10.5072 3.45362C10.501 3.3763 10.4795 3.30097 10.4438 3.23208C10.408 3.16319 10.3588 3.10212 10.2991 3.05247C10.2393 3.00282 10.1702 2.96558 10.0958 2.94296C10.0214 2.92033 9.94322 2.91277 9.86585 2.92071C9.78848 2.92866 9.71348 2.95196 9.64527 2.98924C9.57706 3.02652 9.51701 3.07702 9.46867 3.13778L7.2302 5.81607C7.11028 5.95956 6.88973 5.95955 6.76981 5.81606L4.53162 3.13778C4.48299 3.07789 4.4229 3.02824 4.35485 2.9917C4.2868 2.95517 4.21215 2.93248 4.13523 2.92496C4.05832 2.91744 3.98067 2.92524 3.9068 2.94789C3.83293 2.97055 3.76432 3.00762 3.70494 3.05694C3.64556 3.10627 3.5966 3.16687 3.56091 3.23522C3.52522 3.30358 3.5035 3.37832 3.49702 3.45512C3.49055 3.53191 3.49944 3.60922 3.52318 3.68256C3.54693 3.75589 3.58505 3.8238 3.63533 3.88232L6.08003 6.8077C6.17309 6.91907 6.17309 7.08109 6.08003 7.19245L3.63533 10.1178C3.58412 10.1762 3.54513 10.2442 3.52066 10.3178C3.49619 10.3915 3.48673 10.4692 3.49284 10.5465C3.49895 10.6239 3.52051 10.6992 3.55625 10.7681C3.59198 10.837 3.64118 10.898 3.70093 10.9477C3.76068 10.9973 3.82979 11.0346 3.90419 11.0572C3.97859 11.0798 4.05678 11.0874 4.13415 11.0794C4.21152 11.0715 4.28652 11.0482 4.35473 11.0109C4.42294 10.9736 4.48299 10.9231 4.53133 10.8624L6.76981 8.18407C6.88973 8.04058 7.11027 8.04058 7.23019 8.18407L9.46867 10.8624C9.51701 10.9231 9.57706 10.9736 9.64527 11.0109C9.71348 11.0482 9.78848 11.0715 9.86585 11.0794C9.94322 11.0874 10.0214 11.0798 10.0958 11.0572C10.1702 11.0346 10.2393 10.9973 10.2991 10.9477C10.3588 10.898 10.408 10.837 10.4438 10.7681C10.4795 10.6992 10.501 10.6239 10.5072 10.5465C10.5133 10.4692 10.5038 10.3915 10.4793 10.3178C10.4549 10.2442 10.4159 10.1762 10.3647 10.1178L7.91997 7.19245C7.82691 7.08109 7.82691 6.91907 7.91997 6.8077L10.3647 3.88232Z"
                                      fill="#FF5757"
                                    />
                                  </g>
                                </svg>
                              )}
                              <p className="text-[#074073] ml-2">
                                {guarantor?.status
                                  ? guarantor?.status
                                      ?.charAt(0)
                                      ?.toUpperCase() +
                                    guarantor?.status?.slice(1)?.toLowerCase()
                                  : "N/A"}
                              </p>
                            </div>

                            <div className="flex w-[20%] gap-3">
                              <div
                                onClick={() => {
                                  setSelectedGuarantor(guarantor);
                                  openGuarantorDetailsOpen();
                                }}
                                className="flex items-center cursor-pointer"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                                    fill="#06B217"
                                  />
                                  <path
                                    d="M23.2047 11.745C22.3226 9.46324 20.7912 7.48996 18.7998 6.06906C16.8084 4.64817 14.4443 3.84193 11.9997 3.75C9.55507 3.84193 7.19097 4.64817 5.19958 6.06906C3.20819 7.48996 1.6768 9.46324 0.794681 11.745C0.735106 11.9098 0.735106 12.0902 0.794681 12.255C1.6768 14.5368 3.20819 16.51 5.19958 17.9309C7.19097 19.3518 9.55507 20.1581 11.9997 20.25C14.4443 20.1581 16.8084 19.3518 18.7998 17.9309C20.7912 16.51 22.3226 14.5368 23.2047 12.255C23.2643 12.0902 23.2643 11.9098 23.2047 11.745ZM11.9997 16.875C11.0355 16.875 10.093 16.5891 9.29128 16.0534C8.48959 15.5177 7.86475 14.7564 7.49577 13.8656C7.12679 12.9748 7.03025 11.9946 7.21835 11.0489C7.40646 10.1033 7.87075 9.23464 8.55254 8.55285C9.23432 7.87107 10.103 7.40677 11.0486 7.21867C11.9943 7.03057 12.9745 7.12711 13.8653 7.49609C14.7561 7.86506 15.5174 8.48991 16.0531 9.2916C16.5888 10.0933 16.8747 11.0358 16.8747 12C16.8727 13.2923 16.3584 14.5311 15.4446 15.445C14.5308 16.3588 13.292 16.873 11.9997 16.875Z"
                                    fill="#06B217"
                                  />
                                </svg>
                              </div>
                              {guarantor?.status?.toLowerCase() === "pending" &&
                                loanCustomer?.loan_status?.toLowerCase() ===
                                  "pending" && (
                                  <>
                                    <div
                                      onClick={() => {
                                        setSelectedGuarantor(guarantor);
                                        openAcceptGuarantorship();
                                      }}
                                      className="flex items-center cursor-pointer"
                                    >
                                      <svg
                                        width="20"
                                        height="15"
                                        viewBox="0 0 20 15"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M18.3301 0L19.5117 1.17188L6.42578 14.2676L0 7.8418L1.18164 6.66016L6.42578 11.9043L18.3301 0Z"
                                          fill="#0C722F"
                                        />
                                      </svg>
                                    </div>
                                    <div
                                      onClick={() => {
                                        setSelectedGuarantor(guarantor);
                                        openRejectGuarantorship();
                                      }}
                                      className="flex items-center cursor-pointer"
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M0.75 14.75L7.75 7.75M7.75 7.75L14.75 0.75M7.75 7.75L0.75 0.75M7.75 7.75L14.75 14.75"
                                          stroke="#FF3232"
                                          stroke-width="1.5"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        />
                                      </svg>
                                    </div>
                                  </>
                                )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-700 mt-4 text-lg font-bold flex justify-center items-center h-32">
                          You have no guarantors.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col justify-end items-center h-[42vh]">
                      <svg
                        width="136"
                        height="136"
                        viewBox="0 0 136 136"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M53.4344 14.3176L71.6092 23.6649L91.131 17.6156L99.0454 36.4586L117.888 44.373L111.839 63.8948L121.186 82.0696L104.004 93.1358L99.4819 113.067L79.2062 110.499L62.9307 122.861L49.0489 107.861L28.6354 106.869L27.643 86.4551L12.6432 72.5733L25.0046 56.2978L22.4371 36.0221L42.3682 31.5L53.4344 14.3176Z"
                          fill="#888888"
                        />
                        <path
                          d="M55.1372 20.3457L71.2632 28.6392L88.5843 23.2719L95.6065 39.9907L112.325 47.0129L106.958 64.334L115.252 80.46L100.006 90.2787L95.9938 107.963L78.0038 105.685L63.563 116.653L51.2461 103.344L33.1339 102.463L32.2533 84.3511L18.9445 72.0342L29.9123 57.5934L27.6343 39.6034L45.3185 35.5911L55.1372 20.3457Z"
                          fill="#C4C4C4"
                        />
                        <path
                          d="M85.5939 83.0302C85.7623 83.1986 85.8959 83.3985 85.987 83.6185C86.0782 83.8386 86.1251 84.0744 86.1251 84.3125C86.1251 84.5507 86.0782 84.7865 85.987 85.0065C85.8959 85.2266 85.7623 85.4265 85.5939 85.5949C85.4255 85.7633 85.2256 85.8969 85.0056 85.988C84.7855 86.0791 84.5497 86.126 84.3116 86.126C84.0734 86.126 83.8376 86.0791 83.6176 85.988C83.3975 85.8969 83.1976 85.7633 83.0292 85.5949L67.9991 70.5625L52.9689 85.5949C52.6288 85.935 52.1675 86.126 51.6866 86.126C51.2056 86.126 50.7443 85.935 50.4042 85.5949C50.0641 85.2548 49.873 84.7935 49.873 84.3125C49.873 83.8316 50.0641 83.3703 50.4042 83.0302L65.4366 68L50.4042 52.9699C50.0641 52.6298 49.873 52.1685 49.873 51.6875C49.873 51.2066 50.0641 50.7453 50.4042 50.4052C50.7443 50.0651 51.2056 49.874 51.6866 49.874C52.1675 49.874 52.6288 50.0651 52.9689 50.4052L67.9991 65.4376L83.0292 50.4052C83.3693 50.0651 83.8306 49.874 84.3116 49.874C84.7925 49.874 85.2538 50.0651 85.5939 50.4052C85.934 50.7453 86.1251 51.2066 86.1251 51.6875C86.1251 52.1685 85.934 52.6298 85.5939 52.9699L70.5615 68L85.5939 83.0302Z"
                          fill="white"
                        />
                      </svg>
                      <p className="text-[15px] font-[400] text-[#000000] text-center">
                        This Loan does not require guarantors
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activePage === "actions" && (
                <div className="">
                  <p className="text-[15px] font-[300] text-[#000000]">
                    Select what action you wish to perform on this loan
                  </p>
                  <div className="flex items-center justify-between mt-10">
                    {(loanType === "Guarantor_loan" ||
                      loanType === "Development_loan" ||
                      loanType === "Jijenge_loan") &&
                      progress < 3 &&
                      !hasApproved &&
                      loanCustomer?.loan_status === "Pending Approval" && (
                        <div
                          onClick={openDetailsPageOpen}
                          className="flex items-center justify-between w-[49%] px-2 h-[69px] rounded-[5px] border border-[#C0C3C2] border-[0.5px] cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center rounded-full bg-[#F2F7F6] h-[34px] w-[34px]">
                              <svg
                                width="19"
                                height="19"
                                viewBox="0 0 19 19"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M13.7521 15.6962L16.0005 13.4827L15.5049 12.9863L13.7521 14.7145L12.9961 13.9427L12.4997 14.443L13.7521 15.6962ZM5.35966 6.91188H13.6421V6.12021H5.35887L5.35966 6.91188ZM14.2501 17.5083C13.3682 17.5083 12.62 17.2009 12.0057 16.586C11.3908 15.9717 11.0834 15.2236 11.0834 14.3417C11.0834 13.4598 11.3908 12.7114 12.0057 12.0965C12.6206 11.4816 13.3687 11.1745 14.2501 11.175C15.1315 11.1755 15.8799 11.4827 16.4952 12.0965C17.1106 12.7103 17.4178 13.4587 17.4167 14.3417C17.4167 15.2231 17.1096 15.9712 16.4952 16.586C15.8799 17.2009 15.1315 17.5083 14.2501 17.5083ZM3.16675 16.442V4.44584C3.16675 4.09117 3.2913 3.78928 3.54041 3.54017C3.78953 3.29106 4.09141 3.1665 4.44608 3.1665H14.5549C14.9085 3.1665 15.2101 3.29106 15.4597 3.54017C15.7089 3.78928 15.8334 4.09117 15.8334 4.44584V9.1515C15.3626 8.99687 14.8802 8.91743 14.3862 8.91321C13.8922 8.90899 13.4054 8.97259 12.9256 9.104H5.35887V9.89567H11.1475C10.7507 10.1706 10.4007 10.4934 10.0978 10.8639C9.79485 11.2344 9.54521 11.6424 9.34887 12.0878H5.35966V12.8795H9.08841C9.03141 13.0927 8.98708 13.3078 8.95542 13.5247C8.92375 13.7416 8.90765 13.968 8.90712 14.2039C8.90712 14.5654 8.94486 14.9241 9.02033 15.2798C9.0958 15.6355 9.20928 15.9783 9.36075 16.3082L9.33383 16.3351L8.43529 15.6812L7.36971 16.442L6.30412 15.6812L5.23854 16.442L4.17216 15.6812L3.16675 16.442Z"
                                  fill="#074073"
                                />
                              </svg>
                            </div>
                            <div className="flex flex-col">
                              <p className="text-[#074073] text-[14px] font-[500]">
                                Approve or Decline Loan
                              </p>
                              <p className="text-[12px] text-[#737373] font-[400]">
                                Evaluate and decide on loan applications.
                              </p>
                            </div>
                          </div>
                          <svg
                            width="7"
                            height="12"
                            viewBox="0 0 7 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1.06641 11.6797C1.3584 11.6797 1.59326 11.5781 1.79639 11.375L6.57617 6.71582C6.84912 6.44287 6.96973 6.17627 6.97607 5.83984C6.97607 5.50977 6.84912 5.23047 6.57617 4.96387L1.80273 0.29834C1.59326 0.101562 1.35205 0 1.06641 0C0.482422 0 0 0.469727 0 1.05371C0 1.3457 0.126953 1.6123 0.342773 1.83447L4.48779 5.84619L0.342773 9.84521C0.120605 10.0674 0 10.334 0 10.626C0 11.2036 0.482422 11.6797 1.06641 11.6797Z"
                              fill="black"
                            />
                          </svg>
                        </div>
                      )}
                    <PermissionGate required={[PERMISSIONS?.LN?.DISBURSE]}>
                      {(([
                        "Guarantor_loan",
                        "Development_loan",
                        "Jijenge_loan",
                      ]?.includes(loanType) &&
                        loanCustomer?.loan_status === "Approved") ||
                        (loanType === "Emergency_loan" &&
                          loanCustomer?.loan_status === "Pending") ||
                        (loanType === "Business_loan" &&
                          loanCustomer?.loan_status === "Pending")) && (
                        <div
                          onClick={openPrepareLoanDisbursementModalOpen}
                          className="flex items-center justify-between w-[49%] px-2 h-[69px] rounded-[5px] border border-[#C0C3C2] border-[0.5px] cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center rounded-full bg-[#F2F7F6] h-[34px] w-[34px]">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M14.03 0.742337C14.792 0.476087 15.524 1.20809 15.2577 1.97009L10.814 14.6676C10.5252 15.4911 9.3777 15.5376 9.0237 14.7403L6.87945 9.91634L9.89745 6.89759C9.99681 6.79096 10.0509 6.64992 10.0483 6.5042C10.0458 6.35847 9.98673 6.21943 9.88367 6.11637C9.78061 6.01331 9.64157 5.95428 9.49584 5.9517C9.35012 5.94913 9.20908 6.00323 9.10245 6.10259L6.0837 9.12059L1.2597 6.97634C0.462452 6.62159 0.509702 5.47484 1.33245 5.18609L14.03 0.742337Z"
                                  fill="#074073"
                                />
                              </svg>
                            </div>
                            <div className="flex flex-col">
                              <p className="text-[#074073] text-[14px] font-[500]">
                                Disburse Loan
                              </p>
                              <p className="text-[12px] text-[#737373] font-[400]">
                                Release funds to the customer's account or
                                <br />
                                mobile wallet.
                              </p>
                            </div>
                          </div>
                          <svg
                            width="7"
                            height="12"
                            viewBox="0 0 7 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1.06641 11.6797C1.3584 11.6797 1.59326 11.5781 1.79639 11.375L6.57617 6.71582C6.84912 6.44287 6.96973 6.17627 6.97607 5.83984C6.97607 5.50977 6.84912 5.23047 6.57617 4.96387L1.80273 0.29834C1.59326 0.101562 1.35205 0 1.06641 0C0.482422 0 0 0.469727 0 1.05371C0 1.3457 0.126953 1.6123 0.342773 1.83447L4.48779 5.84619L0.342773 9.84521C0.120605 10.0674 0 10.334 0 10.626C0 11.2036 0.482422 11.6797 1.06641 11.6797Z"
                              fill="black"
                            />
                          </svg>
                        </div>
                      )}
                    </PermissionGate>
                    <div className="flex items-center justify-between w-[49%] px-2 h-[69px] rounded-[5px] border border-[#C0C3C2] border-[0.5px]">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center rounded-full bg-[#F2F7F6] h-[34px] w-[34px]">
                          <svg
                            width="17"
                            height="17"
                            viewBox="0 0 17 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.625 12.75C10.625 13.3136 10.4011 13.8541 10.0026 14.2526C9.60409 14.6511 9.06359 14.875 8.5 14.875C7.93641 14.875 7.39591 14.6511 6.9974 14.2526C6.59888 13.8541 6.375 13.3136 6.375 12.75C6.375 12.1864 6.59888 11.6459 6.9974 11.2474C7.39591 10.8489 7.93641 10.625 8.5 10.625C9.06359 10.625 9.60409 10.8489 10.0026 11.2474C10.4011 11.6459 10.625 12.1864 10.625 12.75Z"
                              fill="#074073"
                            />
                            <path
                              d="M8.49994 2.125C7.23187 2.125 6.01574 2.62874 5.11909 3.5254C4.22243 4.42205 3.71869 5.63818 3.71869 6.90625V9.45731L2.69444 12.0222C2.66236 12.1027 2.65048 12.1899 2.65984 12.2761C2.66921 12.3623 2.69952 12.4449 2.74815 12.5167C2.79677 12.5885 2.86223 12.6473 2.9388 12.6879C3.01537 12.7286 3.10074 12.7499 3.18744 12.75H13.8124C13.8991 12.7499 13.9845 12.7286 14.0611 12.6879C14.1377 12.6473 14.2031 12.5885 14.2517 12.5167C14.3004 12.4449 14.3307 12.3623 14.34 12.2761C14.3494 12.1899 14.3375 12.1027 14.3054 12.0222L13.2812 9.45625V6.90625C13.2812 5.63818 12.7775 4.42205 11.8808 3.5254C10.9841 2.62874 9.76801 2.125 8.49994 2.125Z"
                              fill="#074073"
                            />
                          </svg>
                        </div>
                        <div className="flex flex-col">
                          <p className="text-[#074073] text-[14px] font-[500]">
                            Send Notifications
                          </p>
                          <p className="text-[12px] text-[#737373] font-[400]">
                            Update customers about payments, approvals, or
                            <br />
                            overdue amounts.
                          </p>
                        </div>
                      </div>
                      <svg
                        width="7"
                        height="12"
                        viewBox="0 0 7 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.06641 11.6797C1.3584 11.6797 1.59326 11.5781 1.79639 11.375L6.57617 6.71582C6.84912 6.44287 6.96973 6.17627 6.97607 5.83984C6.97607 5.50977 6.84912 5.23047 6.57617 4.96387L1.80273 0.29834C1.59326 0.101562 1.35205 0 1.06641 0C0.482422 0 0 0.469727 0 1.05371C0 1.3457 0.126953 1.6123 0.342773 1.83447L4.48779 5.84619L0.342773 9.84521C0.120605 10.0674 0 10.334 0 10.626C0 11.2036 0.482422 11.6797 1.06641 11.6797Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-10">
                    <PermissionGate required={[PERMISSIONS?.LN?.UPDATE_TERMS]}>
                      <div className="flex items-center justify-between w-[49%] px-2 h-[69px] rounded-[5px] border border-[#C0C3C2] border-[0.5px]">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center rounded-full bg-[#F2F7F6] h-[34px] w-[34px]">
                            <svg
                              width="17"
                              height="17"
                              viewBox="0 0 17 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10.625 12.75C10.625 13.3136 10.4011 13.8541 10.0026 14.2526C9.60409 14.6511 9.06359 14.875 8.5 14.875C7.93641 14.875 7.39591 14.6511 6.9974 14.2526C6.59888 13.8541 6.375 13.3136 6.375 12.75C6.375 12.1864 6.59888 11.6459 6.9974 11.2474C7.39591 10.8489 7.93641 10.625 8.5 10.625C9.06359 10.625 9.60409 10.8489 10.0026 11.2474C10.4011 11.6459 10.625 12.1864 10.625 12.75Z"
                                fill="#074073"
                              />
                              <path
                                d="M8.49994 2.125C7.23187 2.125 6.01574 2.62874 5.11909 3.5254C4.22243 4.42205 3.71869 5.63818 3.71869 6.90625V9.45731L2.69444 12.0222C2.66236 12.1027 2.65048 12.1899 2.65984 12.2761C2.66921 12.3623 2.69952 12.4449 2.74815 12.5167C2.79677 12.5885 2.86223 12.6473 2.9388 12.6879C3.01537 12.7286 3.10074 12.7499 3.18744 12.75H13.8124C13.8991 12.7499 13.9845 12.7286 14.0611 12.6879C14.1377 12.6473 14.2031 12.5885 14.2517 12.5167C14.3004 12.4449 14.3307 12.3623 14.34 12.2761C14.3494 12.1899 14.3375 12.1027 14.3054 12.0222L13.2812 9.45625V6.90625C13.2812 5.63818 12.7775 4.42205 11.8808 3.5254C10.9841 2.62874 9.76801 2.125 8.49994 2.125Z"
                                fill="#074073"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-[#074073] text-[14px] font-[500]">
                              Update Loan Terms
                            </p>
                            <p className="text-[12px] text-[#737373] font-[400]">
                              Adjust interest rates, repayment schedules, or
                              <br />
                              tenure based on customer requests.
                            </p>
                          </div>
                        </div>
                        <svg
                          width="7"
                          height="12"
                          viewBox="0 0 7 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1.06641 11.6797C1.3584 11.6797 1.59326 11.5781 1.79639 11.375L6.57617 6.71582C6.84912 6.44287 6.96973 6.17627 6.97607 5.83984C6.97607 5.50977 6.84912 5.23047 6.57617 4.96387L1.80273 0.29834C1.59326 0.101562 1.35205 0 1.06641 0C0.482422 0 0 0.469727 0 1.05371C0 1.3457 0.126953 1.6123 0.342773 1.83447L4.48779 5.84619L0.342773 9.84521C0.120605 10.0674 0 10.334 0 10.626C0 11.2036 0.482422 11.6797 1.06641 11.6797Z"
                            fill="black"
                          />
                        </svg>
                      </div>
                    </PermissionGate>

                    <PermissionGate required={[PERMISSIONS?.LN?.EXPORT]}>
                      <div className="flex items-center justify-between w-[49%] px-2 h-[69px] rounded-[5px] border border-[#C0C3C2] border-[0.5px]">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center rounded-full bg-[#F2F7F6] h-[34px] w-[34px]">
                            <svg
                              width="16"
                              height="20"
                              viewBox="0 0 16 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9 7H14.5L9 1.5V7ZM2 0H10L16 6V18C16 18.5304 15.7893 19.0391 15.4142 19.4142C15.0391 19.7893 14.5304 20 14 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V2C0 0.89 0.89 0 2 0ZM3 18H5V12H3V18ZM7 18H9V10H7V18ZM11 18H13V14H11V18Z"
                                fill="#074073"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-[#074073] text-[14px] font-[500]">
                              Generate Reports
                            </p>
                            <p className="text-[12px] text-[#737373] font-[400]">
                              Produce reports for compliance, audits, or
                              <br /> performance reviews.
                            </p>
                          </div>
                        </div>
                        <svg
                          width="7"
                          height="12"
                          viewBox="0 0 7 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1.06641 11.6797C1.3584 11.6797 1.59326 11.5781 1.79639 11.375L6.57617 6.71582C6.84912 6.44287 6.96973 6.17627 6.97607 5.83984C6.97607 5.50977 6.84912 5.23047 6.57617 4.96387L1.80273 0.29834C1.59326 0.101562 1.35205 0 1.06641 0C0.482422 0 0 0.469727 0 1.05371C0 1.3457 0.126953 1.6123 0.342773 1.83447L4.48779 5.84619L0.342773 9.84521C0.120605 10.0674 0 10.334 0 10.626C0 11.2036 0.482422 11.6797 1.06641 11.6797Z"
                            fill="black"
                          />
                        </svg>
                      </div>
                    </PermissionGate>
                  </div>

                  <div className="flex items-center justify-between mt-10">
                    <div className="flex items-center justify-between w-[49%] px-2 h-[69px] rounded-[5px] border border-[#C0C3C2] border-[0.5px]">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center rounded-full bg-[#F2F7F6] h-[34px] w-[34px]">
                          <svg
                            width="19"
                            height="20"
                            viewBox="0 0 19 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.46 7.41L11 5.38V3.12L12.71 1.41L11.29 0L9.99995 1.29L8.70995 0L7.28995 1.41L8.99995 3.12V5.38L6.49995 6.82L4.49995 5.69L3.91995 3.36L1.99995 3.88L2.46995 5.65L0.699951 6.12L1.21995 8.05L3.54995 7.43L5.54995 8.56V11.45L3.54995 12.58L1.21995 11.96L0.699951 13.89L2.46995 14.36L1.99995 16.12L3.92995 16.64L4.54995 14.31L6.54995 13.18L8.99995 14.62V16.88L7.28995 18.59L8.70995 20L9.99995 18.71L11.29 20L12.7 18.59L11 16.88V14.62L14.46 12.61M7.49995 8.56L9.99995 7.11L12.5 8.56V11.44L9.99995 12.89L7.49995 11.44M17 11V5H19V11H17ZM17 15V13H19V15H17Z"
                              fill="#074073"
                            />
                          </svg>
                        </div>
                        <div className="flex flex-col">
                          <p className="text-[#074073] text-[14px] font-[500]">
                            Freeze/Cancel Loan
                          </p>
                          <p className="text-[12px] text-[#737373] font-[400]">
                            Suspend or terminate a loan due to fraud, default,
                            or other issues.
                          </p>
                        </div>
                      </div>
                      <svg
                        width="7"
                        height="12"
                        viewBox="0 0 7 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.06641 11.6797C1.3584 11.6797 1.59326 11.5781 1.79639 11.375L6.57617 6.71582C6.84912 6.44287 6.96973 6.17627 6.97607 5.83984C6.97607 5.50977 6.84912 5.23047 6.57617 4.96387L1.80273 0.29834C1.59326 0.101562 1.35205 0 1.06641 0C0.482422 0 0 0.469727 0 1.05371C0 1.3457 0.126953 1.6123 0.342773 1.83447L4.48779 5.84619L0.342773 9.84521C0.120605 10.0674 0 10.334 0 10.626C0 11.2036 0.482422 11.6797 1.06641 11.6797Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AcceptGuarantorship
        id="id_R"
        isAcceptGuarantorship={isAcceptGuarantorship}
        closeAcceptGuarantorship={closeAcceptGuarantorship}
        selectedGuarantor={selectedGuarantor}
        guaranteeAmount={guaranteeAmount}
        setGuaranteeAmount={setGuaranteeAmount}
        uploadeDocfile={uploadeDocfile}
        setUploadeDocfile={setUploadeDocfile}
        handleSubmitGuarantorResponseAccepted={
          handleSubmitGuarantorResponseAccepted
        }
      />

      <AcceptSuccess
        isAcceptSuccessOpen={isAcceptSuccessOpen}
        closeAcceptSuccess={closeAcceptSuccess}
      />

      <RejectGuarantorship
        id="id_R"
        isRejectGuarantorshipOpen={isRejectGuarantorshipOpen}
        closeRejectGuarantorship={closeRejectGuarantorship}
        handleSubmitGuarantorResponse={handleSubmitGuarantorResponse}
      />

      <RejectSuccess
        isRejectSuccessOpen={isRejectSuccessOpen}
        closeRejectSuccess={closeRejectSuccess}
      />

      <GuarantorDetails
        id="id_R"
        closeGuarantorDetailsOpen={closeGuarantorDetailsOpen}
        isGuarantorDetailsOpen={isGuarantorDetailsOpen}
        openRemoveGuarantor={openRemoveGuarantor}
        openOverrideGuarantor={openOverrideGuarantor}
        selectedGuarantor={selectedGuarantor}
        formattedDate={formattedDate}
      />

      <RemoveGuarantor
        id="id"
        isRemoveGuarantorOpen={isRemoveGuarantorOpen}
        closeRemoveGuarantor={closeRemoveGuarantor}
        setIsWarningRemoveGuarantorOpen={setIsWarningRemoveGuarantorOpen}
      />

      <WarningRemoveGuarantor
        id="id"
        isWarningRemoveGuarantorOpen={isWarningRemoveGuarantorOpen}
        closeWarningRemoveGuarantor={closeWarningRemoveGuarantor}
        handleDeleteGuarantor={handleDeleteGuarantor}
        selectedGuarantor={selectedGuarantor}
      />

      <GuarantorSuccess
        id="id"
        isGuarantorSuccessOpen={isGuarantorSuccessOpen}
        closeGuarantorSuccess={closeGuarantorSuccess}
        selectedGuarantor={selectedGuarantor}
      />

      <OverrideGuarantor
        id="id"
        isOverrideGuarantorOpen={isOverrideGuarantorOpen}
        closeOverrideGuarantor={closeOverrideGuarantor}
        openStatusDetails={openStatusDetails}
      />

      <StatusDetails
        id="id"
        isStatusDetailsOpen={isStatusDetailsOpen}
        closeStatusDetails={closeStatusDetails}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        openOverrideGuarantor={openOverrideGuarantor}
        openReasonsForOverriding={openReasonsForOverriding}
        selectedGuarantor={selectedGuarantor}
      />

      <ReasonsForOverriding
        id="id"
        isReasonsForOverridingOpen={isReasonsForOverridingOpen}
        closeReasonsForOverriding={closeReasonsForOverriding}
        openStatusDetails={openStatusDetails}
        note={note}
        setNote={setNote}
        openOverrideWarning={openOverrideWarning}
      />

      <OverrideWarning
        id="id"
        isOverrideWarningOpen={isOverrideWarningOpen}
        closeOverrideWarning={closeOverrideWarning}
        handleOverrideGuarantorStatus={handleOverrideGuarantorStatus}
      />

      <LoadingButtons isLoadingButtons={isLoadingButtons} />

      <OverrideSuccess
        isOverrideSuccessOpen={isOverrideSuccessOpen}
        closeOverrideSuccess={closeOverrideSuccess}
        selectedGuarantor={selectedGuarantor}
      />

      <AmountReview
        id="id_R"
        isAmountReviewOpen={isAmountReviewOpen}
        closeAmountReviewOpen={closeAmountReviewOpen}
        openLowerLoanAmount={openLowerLoanAmount}
        openDetailsPageOpen={openDetailsPageOpen}
        openLoanChecklistOpen={openLoanChecklistOpen}
        lowerAmount={lowerAmount}
        progress={progress}
      />

      <LowerLoanAmount
        id="id_R"
        isLowerLoanAmountOpen={isLowerLoanAmountOpen}
        closeLowerLoanAmount={closeLowerLoanAmount}
        lowerAmount={lowerAmount}
        setLowerAmount={setLowerAmount}
        loanCustomer={loanCustomer}
        handleCancelButtonClick={handleCancelButtonClick}
      />

      <ApproveLoanAmount
        id="id_R"
        isApproveLoanAmountOpen={isApproveLoanAmountOpen}
        closeApproveLoanAmount={closeApproveLoanAmount}
        handleApproveLoan={handleApproveLoan}
      />
      <ApproversDetails
        id="id_R"
        isApproversDetailsOpen={isApproversDetailsOpen}
        closeApproversDetailsOpen={closeApproversDetailsOpen}
        requestApprovals={requestApprovals}
      />

      <SuccessfullyApproved
        id="id_R"
        isSuccessfullyApprovedOpen={isSuccessfullyApprovedOpen}
        closeSuccessfullyApproved={closeSuccessfullyApproved}
      />

      <DetailsPage
        id_R="id_R"
        isDetailsPageOpen={isDetailsPageOpen}
        closeDetailsPageOpen={closeDetailsPageOpen}
        loanCustomer={loanCustomer}
        navigateToCustomer={navigateToCustomer}
        convertDateOfBirth={convertDateOfBirth}
        loanType={loanType}
        guarantors={guarantors}
        convertLoanDates={convertLoanDates}
        setSelectedGuarantor={setSelectedGuarantor}
        openGuarantorDetailsOpen={openGuarantorDetailsOpen}
        openAmountReviewOpen={openAmountReviewOpen}
        openFinalizeDecliningLoanOpen={openFinalizeDecliningLoanOpen}
        customerAccounts={customerAccounts}
      />

      <LoanChecklist
        id="id_R"
        isLoanChecklistOpen={isLoanChecklistOpen}
        closeLoanChecklistOpen={closeLoanChecklistOpen}
        openFinalizeLoanApprovalOpen={openFinalizeLoanApprovalOpen}
        openAmountReviewOpen={openAmountReviewOpen}
        identityVerified={identityVerified}
        setIdentityVerified={setIdentityVerified}
        kycValid={kycValid}
        setKycValid={setKycValid}
        guarantorVerified={guarantorVerified}
        setGuarantorVerified={setGuarantorVerified}
        savingsRequirementMet={savingsRequirementMet}
        setSavingsRequirementMet={setSavingsRequirementMet}
        noOutstandingDefaults={noOutstandingDefaults}
        setNoOutstandingDefaults={setNoOutstandingDefaults}
        membershipTenureMet={membershipTenureMet}
        setMembershipTenureMet={setMembershipTenureMet}
        documentsReviewed={documentsReviewed}
        setDocumentsReviewed={setDocumentsReviewed}
      />

      <FinalizeLoanApproval
        id="id_R"
        isFinalizeLoanApprovalOpen={isFinalizeLoanApprovalOpen}
        closeFinalizeLoanApprovalOpen={closeFinalizeLoanApprovalOpen}
        text={text}
        setText={setText}
        frontFile={frontFile}
        setFrontFile={setFrontFile}
        openLoanChecklistOpen={openLoanChecklistOpen}
        openApproveLoanAmount={openApproveLoanAmount}
      />

      <FinalizeDecliningLoan
        id="id_R"
        isFinalizeDecliningLoanOpen={isFinalizeDecliningLoanOpen}
        closeFinalizeDecliningLoanOpen={closeFinalizeDecliningLoanOpen}
        openDetailsPageOpen={openDetailsPageOpen}
        reason={reason}
        setReason={setReason}
        openDeclineLoan={openDeclineLoan}
      />

      <DeclineLoan
        id="id_R"
        isDeclineLoanOpen={isDeclineLoanOpen}
        closeDeclineLoan={closeDeclineLoan}
        handleDeclineLoan={handleDeclineLoan}
      />

      <DeclineSuccessful
        id="id_R"
        isDeclineSuccessfulOpen={isDeclineSuccessfulOpen}
        closeDeclineSuccessful={closeDeclineSuccessful}
      />
      <MpesaConfirmModal
        isMpesaConfirmModalOpen={isMpesaConfirmModalOpen}
        closeMpesaConfirmModal={closeMpesaConfirmModal}
        handleConfirmMpesaBack={handleConfirmMpesaBack}
        onDisburse={handleDisburse}
        customerDetails={customerDetails}
        mpesaNumber={mpesaNumber}
        setMpesaNumber={setMpesaNumber}
      />
      <ConfirmDisburseModal
        isConfirmDisburseModalOpen={isConfirmDisburseModalOpen}
        closeConfirmDisburseModal={closeConfirmDisburseModal}
        handleConfirmDisburse={handleConfirmDisburse}
        handleConfirmDisburseCancel={handleConfirmDisburseCancel}
      />
      <ConfirmCustomerDisburseModal
        isConfirmCustomerDisburseModalOpen={isConfirmCustomerDisburseModalOpen}
        closeConfirmCustomerDisburseModal={closeConfirmCustomerDisburseModal}
        loanCustomer={loanCustomer}
        mpesaNumber={mpesaNumber}
        disburseLoan={disburseLoan}
        loanCode={loanCode}
        customerId={customerId}
        amount={amount}
        setAmount={setAmount}
      />
      <DisbursementRecordModal
        isDisbursementRecordModalOpen={isDisbursementRecordModalOpen}
        closeDisbursementRecordModal={closeDisbursementRecordModal}
        handleDisbursementRecord={handleDisbursementRecord}
        photoFile={photoFile}
        setPhotoFile={setPhotoFile}
        fileInputFront={fileInputFront}
        handlePhotoFileChange={handlePhotoFileChange}
        bankName={bankName}
        setBankName={setBankName}
        referenceNumber={referenceNumber}
        setReferenceNumber={setReferenceNumber}
        bankAccountNumber={bankAccountNumber}
        setBankAccountNumber={setBankAccountNumber}
        amountDisbursed={amountDisbursed}
        setAmountDisbursed={setAmountDisbursed}
        date={date}
        disbursedAt={disbursedAt}
        setDate={setDate}
        loanCode={loanCode}
        setDisbursedAt={setDisbursedAt}
        handleSubmitBankRecords={handleSubmitBankRecords}
      />

      <SuccessDisburseModal
        isSuccessDisburseModalOpen={isSuccessDisburseModalOpen}
        closeSuccessDisburseModal={closeSuccessDisburseModal}
      />
      <DeclineLoanModal
        isDeclineLoanModalOpen={isDeclineLoanModalOpen}
        onClose={() => setIsDeclineLoanModalOpen(false)}
        finishDeclineDisburse={() => {
          setIsDeclineLoanModalOpen(false);
          setIsConfirmLoanDeclineModalOpen(true);
        }}
        declineText={declineText}
        setDeclineText={setDeclineText}
        declineFile={declineFile}
        setDeclineFile={setDeclineFile}
        isButtonDisabled={isButtonDisabled}
      />

      <PrepareLoanDisbursement
        isPrepareLoanDisbursementModalOpen={isPrepareLoanDisbursementModalOpen}
        closePrepareLoanDisbursementModal={closePrepareLoanDisbursementModal}
        customerDetails={customerDetails}
        convertDateOfBirth={convertDateOfBirth}
        navigateToCustomer={navigateToCustomer}
        loanType={loanType}
        guarantors={guarantors}
        setSelectedGuarantor={setSelectedGuarantor}
        openGuarantorDetailsOpen={openGuarantorDetailsOpen}
        openChooseDisbursementMethodOpen={openChooseDisbursementMethodOpen}
        loanCustomer={loanCustomer}
        convertLoanDates={convertLoanDates}
        mpesaNumber={mpesaNumber}
        declineDisbursment={declineDisbursment}
        approvals={approvals}
      />
      <ChooseDisbursementMethod
        isChooseDisbursementMethodOpen={isChooseDisbursementMethodOpen}
        closeChooseDisbursementMethod={closeChooseDisbursementMethod}
        onSelectDisbursement={handleDisbursementSelection}
        handleChooseBack={handleChooseBack}
        // handleDisbursementMethod={handleDisbursementMethod}
      />
      <ConfirmLoanDeclineModal
        isConfirmLoanDeclineModalOpen={isConfirmLoanDeclineModalOpen}
        closeConfirmLoanDeclineModal={closeConfirmLoanDeclineModal}
        handleSubmit={handleSubmit}
        loanCode={loanCode}
        declineText={declineText}
        declineFile={declineFile}
      />
      <SuccessDeclineModal
        isSuccessDeclineModalOpen={isSuccessDeclineModalOpen}
        closeSuccessDeclineModal={closeSuccessDeclineModal}
      />
      <DisbursingModal isOpen={isDisbursingModalOpen} />
      <SuccessRecordedModal
        isSuccessRecordedModalOpen={isSuccessRecordedModalOpen}
        closeSuccessRecordedModal={closeSuccessRecordedModal}
      />

      <LoanAddTransactions
        id="ID_R"
        isLoanAddTransactionsOpen={isLoanAddTransactionsOpen}
        closeLoanAddTransactionsOpen={closeLoanAddTransactionsOpen}
        loanTransMode={loanTransMode}
        setLoanTransMode={setLoanTransMode}
        loanAmount={loanAmount}
        setLoanAmount={setLoanAmount}
        loanDate={loanDate}
        setLoanDate={setLoanDate}
        loanAccNumber={loanAccNumber}
        setLoanAccNumber={setLoanAccNumber}
        loanRefNumber={loanRefNumber}
        setLoanRefNumber={setLoanRefNumber}
        loanDocFile={loanDocFile}
        setLoanDocFile={setLoanDocFile}
        handleManualLoanPayment={handleManualLoanPayment}
        loansChecked={loansChecked}
        setLoansChecked={setLoansChecked}
      />

      <PaymentAddedSuccessfully
        id="id_R"
        isPaymentAddedSuccessfullyOpen={isPaymentAddedSuccessfullyOpen}
        closePaymentAddedSuccessfully={closePaymentAddedSuccessfully}
      />

      <ViewLoanTransModal
        openViewLoanTransModal={openViewLoanTransModal}
        closeTransactionDetails={closeTransactionDetails}
        singleTransaction={singleTransaction}
        customerDetails={customerDetails}
        openViewReceipt={openViewReceipt}
      />

      <ViewReceipt
        viewReceiptOpen={viewReceiptOpen}
        closeViewReceipt={closeViewReceipt}
        singleTransaction={singleTransaction}
      />

      <UploadFinancialStatement
        isUploadFinancialStatementOpen={isUploadFinancialStatementOpen}
        closeUploadFinancialStatementOpen={closeUploadFinancialStatementOpen}
        statementType={statementType}
        setStatementType={setStatementType}
        statementPassword={statementPassword}
        setStatementPassword={setStatementPassword}
        uploadedFile={uploadedFile}
        setUploadedFile={setUploadedFile}
        handleUploadStatement={handleUploadStatement}
      />

      <AnalyzingStatement isAnalyzingStatement={isAnalyzingStatement} />
      <GeneratingReports isGeneratingReports={isGeneratingReports} />
      <AnalysisSuccesfull
        isAnalysisSuccesfullOpen={isAnalysisSuccesfullOpen}
        navigateToLoanApprasialReport={navigateToLoanApprasialReport}
      />
    </div>
  );
};

export default Loan;
